import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { format, startOfDay, endOfDay } from "date-fns";
import { Router, type RouterLocation } from "@vaadin/router";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/button-group/button-group.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/select/select.js";
import "@awesome.me/webawesome/dist/components/option/option.js";
import "@awesome.me/webawesome/dist/components/textarea/textarea.js";
import "@awesome.me/webawesome/dist/components/checkbox/checkbox.js";
import "@awesome.me/webawesome/dist/components/switch/switch.js";
import "@awesome.me/webawesome/dist/components/radio-group/radio-group.js";
import "@awesome.me/webawesome/dist/components/radio/radio.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/divider/divider.js";
import "../components/app-shell";
import { getDb, saveEntry, deleteEntry, isDateArchived } from "../db";
import type { DebugRenderable } from "../services/debug/debug-service";
import { selectedDateService } from "../services/selected-date";
import type {
  LogEntry,
  EntryContext,
  PeriodTag,
  UserSettings,
  ExerciseType,
  ExerciseIntensity,
  ExerciseData,
  SickDayData,
  DafneMarker,
} from "../types";
import {
  PERIOD_TAG_INFO,
  // DAFNE_MARKERS used for type reference only
  isMarkerPeriod,
  suggestPeriodTagSmart,
  DEFAULT_MEAL_TIMES,
  formatInsulinDose,
  getQATotal,
  getQAGrandTotal,
  calculateIOB,
  DEFAULT_SETTINGS,
  EXERCISE_TYPE_INFO,
  EXERCISE_INTENSITY_INFO,
  getCurrentSickStatus,
  getSickDayRecommendation,
  formatNextCheckTime,
  getNextCheckTime,
  getExerciseRecommendation,
  getRecentExercise,
  getKetoneAlert,
  getBgKetoneAlert,
  getTimestampMs,
} from "../types";
import {
  getMissingSettings,
  calculateQADoseForCarbs,
  calculatePFDoseWithCarbs,
  calculatePFDoseNoCarbs,
  getUnitsPerCPFromSettings,
  formatRatioDisplay,
  PROTEIN_FAT_CONFIG,
} from "../config/dafne";

const ENTRY_CONTEXT_RULES = {
  LIVE_WINDOW_MINUTES: 90,
  DELAYED_WINDOW_MINUTES: 8 * 60,
  FUTURE_TOLERANCE_MINUTES: 10,
} as const;

@customElement("add-entry-page")
export class AddEntryPage extends LitElement implements DebugRenderable {
  @state() private currentDate = new Date();
  @state() private entries: LogEntry[] = [];
  @state() private settings: UserSettings = DEFAULT_SETTINGS;
  @state() private activeIOB = 0;
  @state() private activeCorrectionIOB = 0;
  @state() private activeCarbsOnBoardCP = 0;
  @state() private currentSickStatus: SickDayData | null = null;
  @state() private settingsMissing: string[] = [];

  // Edit mode
  @state() private editId: string | null = null;
  @state() private editEntry: LogEntry | null = null;
  @state() private showDeleteConfirm = false;
  @state() private isLockedEntry = false;
  @state() private isArchivedEntry = false;
  @state() private archiveError = "";

  // Form fields
  @state() private formTime = "";
  @state() private formPeriod: PeriodTag = "B";
  @state() private formGL = "";
  @state() private formCP = "";
  @state() private formQABase = "";
  @state() private formQACorr = "";
  // OZDAFNE Extra Protein/Fat quick-acting insulin (P/F) — handout v4 Aug 2025
  @state() private formPfEnabled = false;
  @state() private formPfPercent: "20" | "30" = "20";
  @state() private formPfBase = "";
  @state() private suggestedPf = 0;
  /**
   * Tracks the last auto-prefilled note string so we can avoid overwriting a
   * user's edits. If `formNote` matches this value, we own it and may rewrite;
   * otherwise the user has edited and we leave it alone.
   */
  private _pfNotesPrefillKey = "";
  @state() private formBIBase = "";
  @state() private formBICorr = "";
  @state() private formKet = "";
  @state() private formNote = "";
  private periodManuallySelected = false;
  @state() private suggestedCorrection = 0;
  @state() private rawCorrection = 0; // Correction before IOB adjustment
  @state() private correctionBlockedByIOB = false; // True if no-stacking mode blocked correction
  @state() private suggestedQABase = 0;
  @state() private calculatedCP = 0; // Grams converted to CP rounded to 0.5
  @state() private entryContext: EntryContext = "live";
  @state() private entryMinutesLag = 0;

  // Exercise/sick impact on insulin (DAFNE rules)
  @state() private exerciseReduction = 0; // Percentage to reduce QA
  @state() private sickDayMultiplier = 1.0; // Multiplier for correction (1.0 = no change)

  // BI duplicate dose warning (DAFNE rules)
  @state() private existingBIDoseCount = 0; // Count of BI doses already logged today
  @state() private biDuplicateWarning = false; // True if would exceed biFrequency

  // Category selections (which sections to show)
  @state() private selectedCategories = new Set<
    "eating" | "quick" | "long" | "ketone" | "sick" | "exercise"
  >();

  // Exercise fields
  @state() private formExerciseType: ExerciseType = "walk";
  @state() private formExerciseIntensity: ExerciseIntensity = "moderate";
  @state() private formExerciseDuration = "";

  // Track markers already used for the selected day (to disable in selector)
  @state() private usedMarkersForDay: Set<DafneMarker> = new Set();

  // Saving state for header save button
  @state() private saving = false;

  // Stale page detection (user left and came back)
  private _pageEnteredAt = 0;
  private _dateTimeManuallyChanged = false;
  @state() private showStalePageDialog = false;
  private _boundVisibilityHandler = this._handleVisibilityChange.bind(this);
  private _boundSaveRequestHandler = this._handleSaveRequest.bind(this);

  static styles = css`
    :host {
      display: block;
    }

    .page-header {
      display: none;
      justify-content: flex-end;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      border-bottom: 1px solid #dee2e6;
      background: white;
    }

    .page-header:has(wa-button) {
      display: flex;
    }

    .page-header h2 {
      margin: 0;
      margin-right: auto;
      font-size: 1.125rem;
      font-weight: 600;
      display: none;
    }

    /* Show title only on large screens where app-shell header is hidden */
    @media (min-width: 1024px) {
      .page-header {
        display: flex;
      }
      .page-header h2 {
        display: block;
      }
    }

    .page-content {
      padding: 0;
    }

    @media (min-width: 640px) {
      .page-content {
        padding: 1rem;
        padding-bottom: 2rem;
      }
    }

    .form-section {
      background: white;
      border-radius: 0;
      border: none;
      border-bottom: 1px solid #dee2e6;
      padding: 0.75rem;
      margin-bottom: 0;
    }

    @media (min-width: 640px) {
      .form-section {
        border-radius: 0.5rem;
        border: 1px solid #dee2e6;
        padding: 1rem;
        margin-bottom: 1rem;
      }
    }

    .form-row {
      margin-bottom: 1rem;
    }

    .form-row:last-child {
      margin-bottom: 0;
    }

    .form-row-inline {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0.75rem;
      margin-bottom: 1rem;
    }

    .form-row-inline.three-cols {
      grid-template-columns: 1fr;
    }

    @media (min-width: 640px) {
      .form-row-inline {
        grid-template-columns: 1fr 1fr;
      }

      .form-row-inline.three-cols {
        grid-template-columns: 1fr 1fr 1fr;
      }
    }

    .form-section label {
      display: block;
      font-size: 0.875rem;
      font-weight: 500;
      margin-bottom: 0.25rem;
      color: #495057;
    }

    .form-section label small {
      font-weight: 400;
      color: #6c757d;
    }

    .primary-capture {
      background: #f8fbff;
      border: none;
      border-bottom: 1px solid #d7e8ff;
      border-radius: 0;
      padding: 0.75rem;
      margin-bottom: 0;
    }

    @media (min-width: 640px) {
      .primary-capture {
        border: 1px solid #d7e8ff;
        border-radius: 0.75rem;
        padding: 1rem;
        margin-bottom: 1rem;
      }
    }

    .primary-title {
      font-size: 0.95rem;
      font-weight: 700;
      color: #0d6efd;
      margin-bottom: 0.25rem;
    }

    .primary-subtitle {
      font-size: 0.82rem;
      color: #5c6b7a;
      margin-bottom: 0.75rem;
    }

    .field-help {
      font-size: 0.75rem;
      color: #6c757d;
      margin-top: 0.35rem;
      line-height: 1.3;
    }

    .primary-gl-input::part(base),
    .primary-carb-input::part(base) {
      min-height: 3.25rem;
      font-size: 1.05rem;
    }

    /* GL input at top */
    .gl-input-section {
      background: white;
      border-bottom: 1px solid #dee2e6;
      padding: 0.75rem;
    }

    @media (min-width: 640px) {
      .gl-input-section {
        border-radius: 0.5rem;
        border: 1px solid #dee2e6;
        margin-bottom: 1rem;
        padding: 1rem;
      }
    }

    .gl-input-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .gl-input-row wa-input {
      flex: 1;
      max-width: 200px;
    }

    .gl-input-row wa-input::part(base) {
      min-height: 3rem;
      font-size: 1.25rem;
      font-weight: 600;
    }

    .gl-unit {
      font-size: 0.875rem;
      color: #6c757d;
    }

    .gl-status {
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      font-size: 0.75rem;
      font-weight: 600;
    }

    .gl-status.hypo {
      background: #f8d7da;
      color: #842029;
    }
    .gl-status.low {
      background: #fff3cd;
      color: #664d03;
    }
    .gl-status.target {
      background: #d1e7dd;
      color: #0f5132;
    }
    .gl-status.elevated {
      background: #fff3cd;
      color: #664d03;
    }
    .gl-status.high {
      background: #f8d7da;
      color: #842029;
    }

    /* Category selector */
    .category-selector {
      background: white;
      border-bottom: 1px solid #dee2e6;
      padding: 0.75rem;
    }

    @media (min-width: 640px) {
      .category-selector {
        border-radius: 0.5rem;
        border: 1px solid #dee2e6;
        margin-bottom: 1rem;
        padding: 1rem;
      }
    }

    .category-label {
      font-size: 0.75rem;
      color: #6c757d;
      margin-bottom: 0.5rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .category-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
    }

    .category-buttons wa-button::part(base) {
      min-width: 70px;
    }

    .category-buttons wa-button[disabled]::part(base) {
      opacity: 0.5;
    }

    /* Dynamic sections */
    .dynamic-section {
      background: white;
      border-bottom: 1px solid #dee2e6;
      padding: 0.75rem;
    }

    @media (min-width: 640px) {
      .dynamic-section {
        border-radius: 0.5rem;
        border: 1px solid #dee2e6;
        margin-bottom: 1rem;
        padding: 1rem;
      }
    }

    .section-header {
      font-size: 0.875rem;
      font-weight: 600;
      color: #212529;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .sick-status-text {
      font-size: 0.875rem;
      color: #666;
      margin-bottom: 0.75rem;
    }

    /* Inline insulin row with total */
    .insulin-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .insulin-row wa-input {
      width: 80px;
    }

    .insulin-row wa-input::part(base) {
      text-align: center;
    }

    .insulin-operator {
      font-weight: 600;
      color: #6c757d;
    }

    .insulin-total {
      margin-left: auto;
      padding: 0.25rem 0.75rem;
      background: #e7f1ff;
      border-radius: 0.25rem;
      font-weight: 600;
      color: #0d6efd;
      white-space: nowrap;
    }

    /* Exercise button groups */
    .exercise-group {
      margin-bottom: 0.75rem;
    }

    .exercise-group-label {
      font-size: 0.75rem;
      color: #6c757d;
      margin-bottom: 0.25rem;
    }

    .eating-toggle {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.5rem;
    }

    .eating-toggle wa-button::part(base) {
      justify-content: center;
      min-height: 2.5rem;
      font-weight: 600;
    }

    .timing-block {
      border: 1px solid #e9ecef;
      border-radius: 0.75rem;
      padding: 0.875rem;
      margin-bottom: 0.875rem;
      background: #fff;
    }

    .timing-summary {
      border: 1px solid #d7e8ff;
      background: #f8fbff;
      border-radius: 0.75rem;
      padding: 0.75rem 0.875rem;
      margin-bottom: 0.875rem;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 0.75rem;
    }

    .timing-summary-main {
      font-size: 0.92rem;
      font-weight: 600;
      color: #1f2d3d;
      margin-bottom: 0.4rem;
    }

    .timing-summary-note {
      font-size: 0.75rem;
      color: #6c757d;
      margin-top: 0.25rem;
      line-height: 1.3;
    }

    .timing-summary .entry-context-row {
      margin-bottom: 0;
    }

    .timing-title {
      font-size: 0.875rem;
      font-weight: 600;
      color: #343a40;
      margin-bottom: 0.625rem;
    }

    .timing-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      margin-bottom: 0.75rem;
    }

    .timing-row label {
      margin: 0;
      font-weight: 500;
    }

    .timing-row wa-input {
      width: auto;
    }

    .timing-row wa-input[type="date"]::part(input) {
      width: 7rem;
    }

    .timing-row wa-input[type="time"]::part(input) {
      width: 6.5rem;
    }

    /* Period button group */
    .period-selector {
      margin-top: 0.5rem;
    }

    .correction-suggestion {
      background: #e7f1ff;
      border-radius: 0.5rem;
      padding: 0.75rem;
      margin-bottom: 1rem;
      font-size: 0.875rem;
    }

    .correction-suggestion .title {
      font-weight: 600;
      margin-bottom: 0.25rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .correction-suggestion .detail {
      color: #495057;
      font-size: 0.75rem;
    }

    .suggestion-readonly {
      font-weight: 600;
      font-size: 0.75rem;
      color: #495057;
      background: #f1f3f5;
      border-radius: 999px;
      padding: 0.15rem 0.5rem;
    }

    .stacking-alert {
      margin-bottom: 1rem;
    }

    .entry-context-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
      flex-wrap: wrap;
    }

    .entry-context-badge {
      display: inline-flex;
      align-items: center;
      border-radius: 999px;
      padding: 0.2rem 0.6rem;
      font-size: 0.7rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }

    .entry-context-badge.live {
      background: #d1e7dd;
      color: #0f5132;
    }

    .entry-context-badge.delayed {
      background: #cff4fc;
      color: #055160;
    }

    .entry-context-badge.backdated {
      background: #fff3cd;
      color: #664d03;
    }

    .entry-context-badge.future {
      background: #f8d7da;
      color: #842029;
    }

    .entry-context-meta {
      font-size: 0.8rem;
      color: #6c757d;
    }

    .form-actions {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
      padding-top: 1rem;
      border-top: 1px solid #dee2e6;
      margin-top: 1rem;
    }

    .review-confirm {
      margin-top: 1rem;
      padding: 0.875rem;
      border: 1px solid #dee2e6;
      border-radius: 0.5rem;
      background: #f8f9fa;
    }

    .review-confirm wa-checkbox::part(base) {
      font-size: 0.875rem;
      color: #212529;
    }

    /* Recent entries section */
    .entries-section {
      margin-top: 1.5rem;
    }

    .date-header {
      font-size: 0.75rem;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-top: 1rem;
      margin-bottom: 0.5rem;
      padding-bottom: 0.25rem;
      border-bottom: 1px solid #e9ecef;
    }

    .date-header:first-of-type {
      margin-top: 0;
    }

    .section-title {
      font-size: 0.875rem;
      font-weight: 600;
      color: #495057;
      margin-bottom: 0.75rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .entry-card {
      background: white;
      border-radius: 0.5rem;
      border: 1px solid #dee2e6;
      padding: 0.75rem 1rem;
      margin-bottom: 0.5rem;
    }

    .entry-card.has-hypo {
      border-left: 3px solid #dc3545;
    }
    .entry-card.has-high-ket {
      border-left: 3px solid #dc3545;
    }

    .entry-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.25rem;
    }

    .entry-time {
      font-weight: 600;
      font-size: 0.875rem;
    }

    .entry-period {
      background: #e7f1ff;
      color: #0d6efd;
      padding: 0.125rem 0.375rem;
      border-radius: 0.25rem;
      font-size: 0.625rem;
      font-weight: 500;
    }

    .entry-data {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
    }

    .entry-field {
      display: flex;
      flex-direction: column;
      min-width: 40px;
    }

    .entry-field .label {
      font-size: 0.5rem;
      color: #6c757d;
      text-transform: uppercase;
    }

    .entry-field .value {
      font-size: 0.875rem;
      font-weight: 500;
    }

    /* GL color coding */
    .gl-hypo {
      color: #dc3545;
      font-weight: 700;
    }
    .gl-low {
      color: #fd7e14;
    }
    .gl-target {
      color: #198754;
    }
    .gl-elevated {
      color: #fd7e14;
    }
    .gl-high {
      color: #dc3545;
    }

    /* Ketone color coding */
    .ket-normal {
      color: #198754;
    }
    .ket-elevated {
      color: #fd7e14;
    }
    .ket-high {
      color: #dc3545;
    }

    .empty-entries {
      color: #6c757d;
      font-size: 0.875rem;
      font-style: italic;
    }

    /* Status buttons (Sick/Exercise) */
    .context-section {
      margin-top: 1rem;
    }

    .context-title {
      font-size: 0.875rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
      color: #495057;
    }

    .context-subtitle {
      font-size: 0.8rem;
      color: #6c757d;
      margin-bottom: 0.75rem;
    }

    .status-buttons {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 1rem;
      flex-wrap: wrap;
    }

    .status-btn {
      flex: 1;
      min-width: 120px;
    }

    .sick-btn {
      --btn-color: #dc3545;
    }
    .better-btn {
      --btn-color: #198754;
    }
    .exercise-btn {
      --btn-color: #0d6efd;
    }

    /* Recommendations panel */
    .recommendations {
      background: #fff3cd;
      border: 1px solid #ffc107;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .recommendations.sick {
      background: #f8d7da;
      border-color: #dc3545;
    }

    .recommendations.exercise {
      background: #cfe2ff;
      border-color: #0d6efd;
    }

    .recommendations.ketone {
      background: #fff3cd;
      border-color: #ffc107;
    }

    .recommendations.ketone-high {
      background: #f8d7da;
      border-color: #dc3545;
    }

    .recommendations.ketone-danger {
      background: #dc3545;
      border-color: #721c24;
      color: white;
    }

    .recommendations.ketone-danger .rec-header,
    .recommendations.ketone-danger .rec-item {
      color: white;
    }

    .recommendations.ketone-danger .warning {
      color: #fff3cd;
    }

    .recommendations.bg-warning {
      background: #fff3cd;
      border-color: #ffc107;
    }

    .recommendations .rec-header {
      font-weight: 600;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .recommendations .rec-item {
      font-size: 0.875rem;
      padding: 0.25rem 0;
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
    }

    .recommendations .rec-item .icon {
      flex-shrink: 0;
    }

    .recommendations .warning {
      color: #dc3545;
      font-weight: 500;
    }

    .recommendations .action {
      color: #495057;
    }

    .recommendations .next-check {
      background: white;
      border-radius: 0.25rem;
      padding: 0.5rem;
      margin-top: 0.5rem;
      font-size: 0.875rem;
    }

    .recommendations .next-check strong {
      color: #0d6efd;
    }

    /* Exercise form section */
    .exercise-section {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .exercise-section .section-label {
      font-weight: 600;
      margin-bottom: 0.75rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .exercise-types {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
    }

    .exercise-type-btn {
      min-width: 70px;
    }

    .intensity-row {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
    }

    /* Ratios bar */
    .ratios-bar {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      background: #f1f5f9;
      border-bottom: 1px solid #e2e8f0;
      font-size: 0.75rem;
      color: #475569;
      flex-wrap: wrap;
    }

    .ratios-bar .ratios-label {
      font-weight: 600;
      color: #334155;
      margin-right: 0.25rem;
    }

    .ratios-bar .ratio-item {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .ratios-bar .ratio-period {
      font-weight: 600;
      color: #64748b;
    }

    .ratios-bar .ratio-value {
      color: #0f172a;
      font-weight: 500;
    }

    .ratios-bar .ratio-value.not-set {
      color: #94a3b8;
      font-style: italic;
    }

    .ratios-bar .ratio-separator {
      color: #cbd5e1;
      margin: 0 0.125rem;
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    await this._loadSettings();
    await this._loadEntries();

    // Only set defaults if not in edit mode
    if (!this.editId) {
      // Reset date explicitly — the @state initializer only runs once per
      // element instance, and Vaadin Router can reuse the element across
      // visits, which would otherwise leak a previously-selected date.
      this.currentDate = new Date();
      this._setDefaultTime();
      this._pageEnteredAt = Date.now();
      this._dateTimeManuallyChanged = false;
    }

    // Listen for page visibility changes (user returns from background)
    document.addEventListener("visibilitychange", this._boundVisibilityHandler);
    // Listen for header save button
    this.addEventListener("save-request", this._boundSaveRequestHandler);
    this._refreshEntryContext();
    await this._calculateActiveIOB();
    this._calculateSuggestions();
    this._checkSickStatus();
    await this._updateUsedMarkers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      "visibilitychange",
      this._boundVisibilityHandler,
    );
    this.removeEventListener("save-request", this._boundSaveRequestHandler);
  }

  private _handleSaveRequest() {
    // Only save if we have glucose data
    if (this._hasGlucose()) {
      this._saveEntry();
    }
  }

  /**
   * Handle visibility change - detect when user returns to the page
   * after being away for a while. Offer to reset the time if unchanged.
   */
  private _handleVisibilityChange() {
    // Only act when page becomes visible again
    if (document.visibilityState !== "visible") return;

    // Skip in edit mode
    if (this.editId) return;

    // Skip if user manually changed date/time
    if (this._dateTimeManuallyChanged) return;

    // Check if we've been gone for more than 2 minutes
    const now = Date.now();
    const elapsedMinutes = (now - this._pageEnteredAt) / (1000 * 60);

    if (elapsedMinutes >= 2) {
      this.showStalePageDialog = true;
    }
  }

  private _handleStalePageReset() {
    this.showStalePageDialog = false;
    this._setDefaultTime();
    this._pageEnteredAt = Date.now();
    this._dateTimeManuallyChanged = false;
  }

  private _handleStalePageKeep() {
    this.showStalePageDialog = false;
    // Mark as manually changed so we don't prompt again
    this._dateTimeManuallyChanged = true;
  }

  // Router lifecycle hook for edit mode
  onAfterEnter(location: RouterLocation) {
    const id = location.params.id as string | undefined;
    if (id) {
      this.editId = id;
      this._loadEntryForEdit(id);
    } else {
      this.editId = null;
      this.editEntry = null;
      this.isLockedEntry = false;
      this.isArchivedEntry = false;
      this.archiveError = "";
    }
  }

  private async _loadEntryForEdit(id: string) {
    try {
      const entry = await getDb().entries.get(id);
      if (entry) {
        this.editEntry = entry;
        this.isLockedEntry = entry.locked === true;
        this.isArchivedEntry = entry.timestamp
          ? await isDateArchived(new Date(entry.timestamp))
          : false;
        // Populate form fields
        this.currentDate = new Date(entry.timestamp);
        this.formTime = format(new Date(entry.timestamp), "HH:mm");
        this.formPeriod = entry.periodTag;
        this.formGL = entry.bloodGlucose?.toString() || "";
        this.formCP = entry.carbs?.toString() || "";
        this.formQABase = entry.qaBase?.toString() || "";
        this.formQACorr = entry.qaCorrection?.toString() || "";
        // P/F restoration — derive enabled state from the dose itself, since
        // pfPercent is undefined for no-carb entries.
        const editPf = entry.pfBase ?? 0;
        this.formPfEnabled = editPf > 0;
        this.formPfBase = editPf > 0 ? editPf.toString() : "";
        this.formPfPercent = entry.pfPercent === 30 ? "30" : "20";
        this._pfNotesPrefillKey = "";
        this.formBIBase = entry.biBase?.toString() || "";
        this.formBICorr = entry.biCorrection?.toString() || "";
        this.formKet = entry.ketones?.toString() || "";
        this.formNote = entry.notes || "";

        // Populate selected categories based on entry data
        const categories = new Set<
          "eating" | "quick" | "long" | "ketone" | "sick" | "exercise"
        >();
        if ((entry.carbs ?? 0) > 0 || (entry.pfBase ?? 0) > 0)
          categories.add("eating");
        if (entry.qaBase || entry.qaCorrection || (entry.pfBase ?? 0) > 0)
          categories.add("quick");
        if (entry.biBase || entry.biCorrection) categories.add("long");
        if (entry.ketones) categories.add("ketone");
        if (entry.sickDay) categories.add("sick");
        if (entry.exercise) categories.add("exercise");
        this.selectedCategories = categories;

        // Exercise
        if (entry.exercise) {
          this.formExerciseType = entry.exercise.type;
          this.formExerciseIntensity = entry.exercise.intensity;
          this.formExerciseDuration = entry.exercise.duration.toString();
        }

        this._refreshEntryContext();
        await this._calculateActiveIOB();
        this._calculateSuggestions();
      }
    } catch (error) {
      console.error("Failed to load entry for edit:", error);
    }
  }

  private async _loadSettings() {
    try {
      const stored = await getDb().settings.get("settings");
      if (stored) this.settings = stored;
    } catch (error) {
      console.error("Failed to load settings:", error);
    }
  }

  private async _loadEntries() {
    const now = new Date();
    // Load past 24 hours of entries for IOB and other calculations
    const past24Hours = now.getTime() - 24 * 60 * 60 * 1000;
    const nowTs = now.getTime();

    // Helper to get timestamp as number (handles string/Date inconsistency)
    const getTs = (t: Date | string | number | unknown): number => {
      if (t instanceof Date) return t.getTime();
      if (typeof t === "string") return new Date(t).getTime();
      if (typeof t === "number") return t;
      return new Date(t as string).getTime();
    };

    try {
      const allEntries = await getDb().entries.toArray();
      this.entries = allEntries
        .filter((e) => {
          if ((e as any)._deletedAt) return false;
          const ts = getTs(e.timestamp);
          return ts >= past24Hours && ts <= nowTs;
        })
        .sort((a, b) => getTs(a.timestamp) - getTs(b.timestamp));
    } catch (error) {
      console.error("Failed to load entries:", error);
    }
  }

  private _checkSickStatus() {
    this.currentSickStatus = getCurrentSickStatus(this.entries);

    // For new entries: if user is currently sick, auto-select sick category
    if (!this.editId && this.currentSickStatus) {
      this.selectedCategories = new Set([...this.selectedCategories, "sick"]);
    }
  }

  /**
   * Check for BI duplicate dose warning (DAFNE rules)
   * Warns if about to exceed biFrequency setting for the day
   */
  private _checkBIDuplicateDose() {
    // Count BI doses in today's entries (excluding current entry if editing)
    const todayStart = startOfDay(this.currentDate).getTime();
    const todayEnd = endOfDay(this.currentDate).getTime();

    this.existingBIDoseCount = this.entries.filter((e) => {
      if (this.editId && e.id === this.editId) return false; // Exclude entry being edited
      const ts = new Date(e.timestamp).getTime();
      const biTotal = (e.biBase || 0) + (e.biCorrection || 0);
      return ts >= todayStart && ts <= todayEnd && biTotal > 0;
    }).length;

    // Check if adding more BI would exceed frequency
    const maxDoses = this.settings.biFrequency === "twice" ? 2 : 1;
    const currentBI = this._getBITotal();

    // Warn if already at/over limit and trying to add more
    this.biDuplicateWarning =
      currentBI > 0 && this.existingBIDoseCount >= maxDoses;
  }

  private _setDefaultTime() {
    const now = new Date();
    this.formTime = format(now, "HH:mm");
    this.periodManuallySelected = false;
    this._applyPredictedPeriod(this.usedMarkersForDay);
  }

  private _normalizeSuggestedPeriod(period: PeriodTag): PeriodTag {
    return isMarkerPeriod(period) ? period : "Other";
  }

  private _applyPredictedPeriod(usedMarkers: Set<DafneMarker>) {
    if (this.editId) return;
    const mealTimes = this.settings.mealTimes ?? DEFAULT_MEAL_TIMES;
    const suggestion = suggestPeriodTagSmart(
      this._getEntryTimestamp(),
      mealTimes,
      [...usedMarkers],
    );
    const normalized = this._normalizeSuggestedPeriod(suggestion.periodTag);
    this.formPeriod =
      isMarkerPeriod(normalized) && usedMarkers.has(normalized)
        ? "Other"
        : normalized;
    this._calculateSuggestions();
  }

  private _ensurePeriodIsNotAlreadyUsed(usedMarkers: Set<DafneMarker>) {
    // Never auto-default to a marker that's already used for the selected day.
    if (
      !this.editId &&
      isMarkerPeriod(this.formPeriod) &&
      usedMarkers.has(this.formPeriod)
    ) {
      this.formPeriod = "Other";
      this._calculateSuggestions();
    }
  }

  /**
   * Build timestamp from currentDate and formTime
   */
  private _getEntryTimestamp(): Date {
    const [hours, minutes] = this.formTime.split(":").map(Number);
    const timestamp = new Date(this.currentDate);
    timestamp.setHours(hours || 0, minutes || 0, 0, 0);
    return timestamp;
  }

  private _isSameLocalDate(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private _getEntryContext(now: Date = new Date()): {
    context: EntryContext;
    minutesLag: number;
  } {
    const timestamp = this._getEntryTimestamp();
    const minutesLag = Math.round(
      (now.getTime() - timestamp.getTime()) / (60 * 1000),
    );

    if (minutesLag < -ENTRY_CONTEXT_RULES.FUTURE_TOLERANCE_MINUTES) {
      return { context: "future", minutesLag };
    }

    const sameDay = this._isSameLocalDate(timestamp, now);
    if (
      sameDay &&
      minutesLag <= ENTRY_CONTEXT_RULES.LIVE_WINDOW_MINUTES &&
      minutesLag >= -ENTRY_CONTEXT_RULES.FUTURE_TOLERANCE_MINUTES
    ) {
      return { context: "live", minutesLag };
    }

    if (
      sameDay &&
      minutesLag > ENTRY_CONTEXT_RULES.LIVE_WINDOW_MINUTES &&
      minutesLag <= ENTRY_CONTEXT_RULES.DELAYED_WINDOW_MINUTES
    ) {
      return { context: "delayed", minutesLag };
    }

    return { context: "backdated", minutesLag };
  }

  private _refreshEntryContext() {
    const { context, minutesLag } = this._getEntryContext();
    this.entryContext = context;
    this.entryMinutesLag = minutesLag;
  }

  private _isSuggestionReadOnlyContext(): boolean {
    return this.entryContext === "backdated" || this.entryContext === "future";
  }

  private _formatLag(minutes: number): string {
    const absMinutes = Math.abs(minutes);
    if (absMinutes < 120) return `${absMinutes} min`;

    const hours = Math.round(absMinutes / 60);
    if (hours < 48) return `${hours} hour${hours === 1 ? "" : "s"}`;

    const days = Math.round(hours / 24);
    return `${days} day${days === 1 ? "" : "s"}`;
  }

  private _getEntryContextLabel(): string {
    switch (this.entryContext) {
      case "live":
        return "Live";
      case "delayed":
        return "Delayed";
      case "backdated":
        return "Backdated";
      case "future":
        return "Future";
    }
  }

  private _getEntryContextMeta(): string {
    if (this.entryContext === "future") {
      return `${this._formatLag(this.entryMinutesLag)} ahead of now`;
    }
    if (this.entryContext === "live") {
      return "Logged close to current time";
    }
    return `${this._formatLag(this.entryMinutesLag)} after event time`;
  }

  /**
   * Handle date input change - recalculate IOB for new entry date
   */
  private async _onDateChange(value: string) {
    // Parse yyyy-MM-dd format
    const [year, month, day] = value.split("-").map(Number);
    if (year && month && day) {
      this.currentDate = new Date(year, month - 1, day);
      this._dateTimeManuallyChanged = true;
      this._refreshEntryContext();
      await this._calculateActiveIOB();
      this._calculateSuggestions();
      await this._updateUsedMarkers();
    }
  }

  /**
   * Handle time input change - recalculate IOB for new entry time
   */
  private async _onTimeChange(value: string) {
    this.formTime = value;
    this._dateTimeManuallyChanged = true;
    this._refreshEntryContext();
    if (!this.editId && !this.periodManuallySelected) {
      this._applyPredictedPeriod(this.usedMarkersForDay);
    } else {
      this._ensurePeriodIsNotAlreadyUsed(this.usedMarkersForDay);
    }
    await this._calculateActiveIOB();
    this._calculateSuggestions();
  }

  /**
   * Update the set of markers already used for the selected day.
   * If editing an entry, exclude that entry's marker from the used set.
   */
  private async _updateUsedMarkers() {
    const dayStart = startOfDay(this.currentDate).getTime();
    const dayEnd = endOfDay(this.currentDate).getTime();

    const getTs = (t: Date | string | number | unknown): number => {
      if (t instanceof Date) return t.getTime();
      if (typeof t === "string") return new Date(t).getTime();
      if (typeof t === "number") return t;
      return new Date(t as string).getTime();
    };

    try {
      const allEntries = await getDb().entries.toArray();
      const usedMarkers = new Set<DafneMarker>();

      for (const entry of allEntries) {
        if ((entry as any)._deletedAt) continue;
        const ts = getTs(entry.timestamp);
        if (ts < dayStart || ts > dayEnd) continue;

        // Don't count the entry being edited
        if (this.editId && entry.id === this.editId) continue;

        // Only count if it's a marker (not Other or legacy after-*)
        if (isMarkerPeriod(entry.periodTag)) {
          usedMarkers.add(entry.periodTag);
        }
      }

      this.usedMarkersForDay = usedMarkers;
      if (!this.editId && !this.periodManuallySelected) {
        this._applyPredictedPeriod(usedMarkers);
      } else {
        this._ensurePeriodIsNotAlreadyUsed(usedMarkers);
      }
    } catch (error) {
      console.error("Failed to check used markers:", error);
      this.usedMarkersForDay = new Set();
    }
  }

  private async _calculateActiveIOB() {
    const CARB_ABSORPTION_HOURS = 2;

    // Use the entry time being created, not current time
    const entryTime = this._getEntryTimestamp();
    const entryTs = entryTime.getTime();
    // Look back 4 hours for IOB calculation (across day boundaries)
    const iobWindowStart = entryTs - 4 * 60 * 60 * 1000;

    // Helper to get timestamp as number (handles string/Date inconsistency)
    const getTs = (t: Date | string | number | unknown): number => {
      if (t instanceof Date) return t.getTime();
      if (typeof t === "string") return new Date(t).getTime();
      if (typeof t === "number") return t;
      return new Date(t as string).getTime();
    };

    try {
      const allEntries = await getDb().entries.toArray();
      const recentEntries = allEntries.filter((e) => {
        if ((e as any)._deletedAt) return false;
        const ts = getTs(e.timestamp);
        return ts >= iobWindowStart && ts <= entryTs;
      });

      let totalIOB = 0;
      let correctionIOB = 0;
      let carbsOnBoardCP = 0;
      for (const entry of recentEntries) {
        // P/F is the same molecule as QA insulin; include its residual in IOB
        // so subsequent corrections don't ignore a still-active P/F dose.
        const qaTotal = getQAGrandTotal(entry);
        if (!qaTotal || qaTotal <= 0) continue;

        const hoursElapsed =
          (entryTs - getTs(entry.timestamp)) / (1000 * 60 * 60);
        totalIOB += calculateIOB(qaTotal, hoursElapsed);

        const hasCarbs = (entry.carbs ?? 0) > 0;
        // Correction IOB excludes P/F: P/F covers the delayed protein/fat
        // rise, not above-target GL. With carbs, only the explicit correction
        // counts. Without carbs, the legacy heuristic treats qaBase+qaCorrection
        // as a correction (no meal to cover) — but P/F is excluded.
        const correctionUnits = hasCarbs
          ? Math.max(0, entry.qaCorrection ?? 0)
          : Math.max(0, (entry.qaBase ?? 0) + (entry.qaCorrection ?? 0));
        if (correctionUnits > 0) {
          correctionIOB += calculateIOB(correctionUnits, hoursElapsed);
        }

        if (hasCarbs) {
          const cp = (entry.carbs ?? 0) / 10;
          const carbFraction = Math.max(
            0,
            1 - hoursElapsed / CARB_ABSORPTION_HOURS,
          );
          carbsOnBoardCP += cp * carbFraction;
        }
      }
      this.activeIOB = Math.round(totalIOB * 10) / 10;
      this.activeCorrectionIOB = Math.round(correctionIOB * 10) / 10;
      this.activeCarbsOnBoardCP = Math.round(carbsOnBoardCP * 10) / 10;
    } catch (error) {
      console.error("Failed to calculate IOB:", error);
      this.activeIOB = 0;
      this.activeCorrectionIOB = 0;
      this.activeCarbsOnBoardCP = 0;
    }
  }

  private _calculateSuggestions() {
    const increment = this.settings.doseIncrement || 0.5;
    const stackingMode = this.settings.correctionStacking || "no-stacking";
    this._refreshEntryContext();

    // Check if settings are configured
    this.settingsMissing = getMissingSettings(this.settings);

    // Calculate exercise impact on QA (DAFNE rules)
    this._calculateExerciseImpact();

    // Calculate sick day impact on corrections (DAFNE rules)
    this._calculateSickDayImpact();

    // Calculate correction based on GL
    const gl = parseFloat(this.formGL);
    this.correctionBlockedByIOB = false;
    this.rawCorrection = 0;

    if (gl && this.settings.correctionFactor) {
      const target =
        (this.settings.targetBgLow + this.settings.targetBgHigh) / 2;
      let rawCorrectionCalc = (gl - target) / this.settings.correctionFactor;

      // Apply sick day multiplier to correction (DAFNE: may need 10-20% more when sick)
      rawCorrectionCalc = rawCorrectionCalc * this.sickDayMultiplier;

      // Round raw correction to dose increment for display
      this.rawCorrection =
        Math.round(rawCorrectionCalc / increment) * increment;

      if (stackingMode === "no-stacking") {
        // DAFNE recommendation: Don't suggest corrections while IOB is active
        if (this.activeIOB > 0 && this.rawCorrection > 0) {
          this.correctionBlockedByIOB = true;
          this.suggestedCorrection = 0;
        } else {
          this.suggestedCorrection = this.rawCorrection;
        }
      } else {
        // IOB-adjusted mode: Subtract IOB from raw correction
        const adjustedCorrection = rawCorrectionCalc - this.activeIOB;
        this.suggestedCorrection =
          Math.round(adjustedCorrection / increment) * increment;
      }
    } else {
      this.suggestedCorrection = 0;
    }

    // Calculate QA base based on carbs and period ratio (DAFNE style)
    const grams = parseFloat(this.formCP);
    if (grams && grams > 0) {
      // Convert grams to DAFNE CP (1 CP = 10g), rounded to 0.5 increments
      this.calculatedCP = Math.round((grams / 10) * 2) / 2;

      const ratioKey =
        PERIOD_TAG_INFO[this.formPeriod]?.ratioFrom || this.formPeriod;
      // Use centralized calculation from config/dafne/insulin.ts
      let baseQA = calculateQADoseForCarbs(
        this.calculatedCP,
        this.settings.cpRatios,
        ratioKey,
        increment,
      );

      // Apply exercise reduction to QA base (DAFNE: reduce insulin before/after exercise)
      if (this.exerciseReduction > 0) {
        baseQA = baseQA * (1 - this.exerciseReduction / 100);
        baseQA = Math.round(baseQA / increment) * increment;
      }

      this.suggestedQABase = baseQA;
    } else {
      this.calculatedCP = 0;
      this.suggestedQABase = 0;
    }

    // OZDAFNE Extra Protein/Fat (P/F) suggested dose — Version 4, Aug 2025
    // - WITH carbs: ceil(suggestedQABase * percent/100) to doseIncrement
    // - WITHOUT carbs: ratio × 1 CP (protein/fat counted as 1 CP)
    if (this.formPfEnabled) {
      const ratioKey =
        PERIOD_TAG_INFO[this.formPeriod]?.ratioFrom || this.formPeriod;
      const grams = parseFloat(this.formCP);
      const hasCarbs = !Number.isNaN(grams) && grams > 0;
      if (hasCarbs && this.suggestedQABase > 0) {
        const pct =
          this.formPfPercent === "30"
            ? PROTEIN_FAT_CONFIG.ESCALATED_PERCENT
            : PROTEIN_FAT_CONFIG.STARTING_PERCENT;
        this.suggestedPf = calculatePFDoseWithCarbs(
          this.suggestedQABase,
          pct,
          increment,
        );
      } else if (!hasCarbs) {
        this.suggestedPf = calculatePFDoseNoCarbs(
          this.settings.cpRatios,
          ratioKey,
          increment,
        );
      } else {
        this.suggestedPf = 0;
      }
    } else {
      this.suggestedPf = 0;
    }

    // Historical/future entries are not suitable for live predictions or
    // recommendations — blank every suggestion so the render sites that key
    // off these values short-circuit and we show only the backdating banner.
    if (this.entryContext === "backdated" || this.entryContext === "future") {
      this.suggestedCorrection = 0;
      this.rawCorrection = 0;
      this.correctionBlockedByIOB = false;
      this.suggestedQABase = 0;
      this.suggestedPf = 0;
    }
  }

  /**
   * Calculate exercise impact on QA insulin (DAFNE rules from OzDAFNE)
   * - Current exercise being logged
   * - Recent exercise from today's entries (post-exercise effect)
   */
  private _calculateExerciseImpact() {
    this.exerciseReduction = 0;

    // Check current exercise being logged
    if (this.selectedCategories.has("exercise") && this.formExerciseDuration) {
      const exercise: ExerciseData = {
        type: this.formExerciseType,
        intensity: this.formExerciseIntensity,
        duration: parseInt(this.formExerciseDuration),
      };
      const rec = getExerciseRecommendation(
        exercise,
        "before",
        this.formPeriod,
      );
      this.exerciseReduction = rec.qaReduction;
      return;
    }

    // Check recent exercise from today's entries (post-exercise effect)
    const recentExercise = getRecentExercise(this.entries);
    if (recentExercise.length > 0) {
      const latest = recentExercise[0];
      if (latest.exercise) {
        const rec = getExerciseRecommendation(
          latest.exercise,
          "after",
          this.formPeriod,
        );
        this.exerciseReduction = rec.qaReduction;
      }
    }
  }

  /**
   * Calculate sick day impact on correction insulin (DAFNE rules)
   * - When sick, may need 10-20% more correction insulin
   */
  private _calculateSickDayImpact() {
    this.sickDayMultiplier = 1.0;

    // Check if sick category is selected
    if (this.selectedCategories.has("sick")) {
      this.sickDayMultiplier = 1.15; // 15% increase per DAFNE guideline
      return;
    }

    // Check if already in sick day mode from previous entries
    if (this.currentSickStatus) {
      this.sickDayMultiplier = 1.15;
    }
  }

  /**
   * Check if this is a context-only entry (DAFNE rules)
   * Context-only = sick or exercise marker WITHOUT insulin data
   * These don't need the "reviewed values" confirmation checkbox
   */
  private _isContextOnlyEntry(): boolean {
    const hasInsulin = this._getQATotal() > 0 || this._getBITotal() > 0;
    const hasCarbs = parseFloat(this.formCP) > 0;
    const hasKetones = parseFloat(this.formKet) > 0;

    // If only sick, exercise, or both selected (no GL-related data)
    const onlyContextCategories =
      (this.selectedCategories.has("sick") ||
        this.selectedCategories.has("exercise")) &&
      !this.selectedCategories.has("eating") &&
      !this.selectedCategories.has("quick") &&
      !this.selectedCategories.has("long") &&
      !this.selectedCategories.has("ketone");

    // Context-only if no insulin, carbs, or ketones and only context categories
    return onlyContextCategories && !hasInsulin && !hasCarbs && !hasKetones;
  }

  private _onGLChange(value: string) {
    this.formGL = value;
    this._calculateSuggestions();
  }

  private _onCPChange(value: string) {
    this.formCP = value;
    this._calculateSuggestions();
  }

  private _onQABaseChange(value: string) {
    this.formQABase = value;
    this._calculateSuggestions();
  }

  private _onQACorrChange(value: string) {
    this.formQACorr = value;
    this._calculateSuggestions();
  }

  private _onPfToggleChange(enabled: boolean) {
    this.formPfEnabled = enabled;
    if (!enabled) {
      // Toggle off → clear the dose so it isn't saved.
      this.formPfBase = "";
      this.suggestedPf = 0;
      // If the notes field still holds our auto-prefill, clear it.
      if (this._pfNotesPrefillKey && this.formNote === this._pfNotesPrefillKey) {
        this.formNote = "";
      }
      this._pfNotesPrefillKey = "";
    } else if (!this.selectedCategories.has("quick")) {
      // P/F is quick-acting insulin — surface the Quick section so the
      // bundled Base + Correction + P/F total is visible alongside it.
      this.selectedCategories = new Set(this.selectedCategories).add("quick");
    }
    this._calculateSuggestions();
    if (enabled) this._maybePrefillPfNotes();
  }

  private _onPfPercentChange(value: "20" | "30") {
    this.formPfPercent = value;
    this._calculateSuggestions();
    this._maybePrefillPfNotes();
  }

  private _onPfBaseChange(value: string) {
    this.formPfBase = value;
    this._maybePrefillPfNotes();
  }

  /**
   * Build the OZDAFNE-style notes template for a P/F entry. Mirrors the
   * handout's example wording (Emma p.13: "Added 20% Extra Protein/Fat
   * quick-acting insulin dose (1 unit) for the large chicken breast stir-fry
   * with veggies and rice." — we omit the meal description).
   */
  private _pfNotesTemplate(): string {
    const dose = parseFloat(this.formPfBase);
    if (Number.isNaN(dose) || dose <= 0) return "";
    const grams = parseFloat(this.formCP);
    const hasCarbs = !Number.isNaN(grams) && grams > 0;
    const unitLabel = dose === 1 ? "unit" : "units";
    if (hasCarbs) {
      return `Added ${this.formPfPercent}% Extra Protein/Fat quick-acting insulin (${dose} ${unitLabel}).`;
    }
    return `Added Extra Protein/Fat quick-acting insulin (${dose} ${unitLabel}) for a high protein/fat meal without carbs.`;
  }

  /** Reset every P/F-related form field. Called from category teardown. */
  private _tearDownPf() {
    this.formPfEnabled = false;
    this.formPfPercent = "20";
    this.formPfBase = "";
    this.suggestedPf = 0;
    if (
      this._pfNotesPrefillKey &&
      this.formNote === this._pfNotesPrefillKey
    ) {
      this.formNote = "";
    }
    this._pfNotesPrefillKey = "";
  }

  /** Prefill notes with the OZDAFNE template if the user hasn't typed their own. */
  private _maybePrefillPfNotes() {
    if (!this.formPfEnabled) return;
    const userOwnsNote =
      this.formNote.length > 0 && this.formNote !== this._pfNotesPrefillKey;
    if (userOwnsNote) return;
    const next = this._pfNotesTemplate();
    if (!next) return;
    this.formNote = next;
    this._pfNotesPrefillKey = next;
  }

  private _onBIBaseChange(value: string) {
    this.formBIBase = value;
    this._checkBIDuplicateDose();
    this._calculateSuggestions();
  }

  private _onBICorrChange(value: string) {
    this.formBICorr = value;
    this._checkBIDuplicateDose();
    this._calculateSuggestions();
  }

  private _onKetoneChange(value: string) {
    this.formKet = value;
    this._calculateSuggestions();
  }

  private _onExerciseTypeChange(type: ExerciseType) {
    this.formExerciseType = type;
    this._calculateSuggestions();
  }

  private _onExerciseIntensityChange(intensity: ExerciseIntensity) {
    this.formExerciseIntensity = intensity;
    this._calculateSuggestions();
  }

  private _onExerciseDurationChange(value: string) {
    this.formExerciseDuration = value;
    this._calculateSuggestions();
  }

  private _hasGlucose(): boolean {
    const gl = parseFloat(this.formGL);
    return !Number.isNaN(gl) && gl > 0;
  }

  private _toggleCategory(
    category: "eating" | "quick" | "long" | "ketone" | "sick" | "exercise",
  ) {
    const newSet = new Set(this.selectedCategories);
    if (newSet.has(category)) {
      newSet.delete(category);
      // Clear related form data when deselecting
      if (category === "eating") {
        this.formCP = "";
        // P/F lives in the Eating section now — tear it down with Eating.
        this._tearDownPf();
      }
      if (category === "quick") {
        this.formQABase = "";
        this.formQACorr = "";
        // P/F is quick-acting insulin: removing Quick removes P/F too.
        this._tearDownPf();
      }
      if (category === "long") {
        this.formBIBase = "";
        this.formBICorr = "";
        this.biDuplicateWarning = false;
      }
      if (category === "ketone") this.formKet = "";
      if (category === "exercise") this.formExerciseDuration = "";
    } else {
      newSet.add(category);
      // Check for BI duplicate when selecting long category
      if (category === "long") {
        this._checkBIDuplicateDose();
      }
    }
    this.selectedCategories = newSet;
    this._calculateSuggestions();
  }

  private _onPeriodChange(value: PeriodTag) {
    this.periodManuallySelected = true;
    this.formPeriod = value;
    this._calculateSuggestions();
  }

  private _cancel() {
    Router.go("/");
  }

  // Use centralized conversion from config/dafne/insulin.ts
  private _getUnitsPerCP(period: string): number {
    return getUnitsPerCPFromSettings(this.settings.cpRatios, period);
  }

  private async _saveEntry(addAnother = false) {
    // Prevent editing locked CGM entries
    if (this.isLockedEntry) {
      console.warn("Cannot save: this is a locked CGM entry");
      return;
    }

    this.archiveError = "";
    this.saving = true;

    const [hours, minutes] = this.formTime.split(":").map(Number);
    const timestamp = new Date(this.currentDate);
    timestamp.setHours(hours, minutes, 0, 0);
    const contextInfo = this._getEntryContext();

    if (contextInfo.context === "future") {
      console.warn("Cannot save entry: timestamp is too far in the future");
      this.saving = false;
      return;
    }

    // Pre-flight check: if the chosen date falls in an archived month,
    // refuse before we touch Dexie. operations.ts also throws, but checking
    // here gives us a clean wa-callout instead of an unhandled rejection.
    if (await isDateArchived(timestamp)) {
      this.archiveError = `This entry is dated ${format(timestamp, "d MMM yyyy")}, which falls within an archived month. Archived data is read-only and cannot be modified.`;
      this.saving = false;
      return;
    }

    // Build exercise data if provided
    let exerciseData: ExerciseData | undefined;
    if (this.selectedCategories.has("exercise") && this.formExerciseDuration) {
      exerciseData = {
        type: this.formExerciseType,
        intensity: this.formExerciseIntensity,
        duration: parseInt(this.formExerciseDuration),
      };
    }

    // Build sick day data if sick category is selected
    let sickDayData: SickDayData | undefined;
    if (this.selectedCategories.has("sick")) {
      sickDayData = {
        status: "sick",
        symptoms: this.formNote || undefined,
      };
    }

    // Capture current ratios for historical review (store as units per CP)
    const ratioKey =
      PERIOD_TAG_INFO[this.formPeriod]?.ratioFrom || this.formPeriod;
    const cpRatioUsed = this._getUnitsPerCP(ratioKey) || undefined;
    const correctionFactorUsed = this.settings.correctionFactor ?? undefined;

    // Extra Protein/Fat (P/F) quick-acting insulin payload — only persisted
    // when the toggle is on and a positive dose has been entered.
    const pfDose = this.formPfEnabled
      ? parseFloat(this.formPfBase)
      : Number.NaN;
    const pfBase =
      Number.isFinite(pfDose) && pfDose > 0 ? pfDose : undefined;
    // pfPercent is meaningful only on the with-carbs path (where the dose is
    // derived from a percentage of the QA base); the no-carbs path has no
    // percentage to record.
    const pfHasCarbs = this.formCP ? parseFloat(this.formCP) > 0 : false;
    const pfPercent =
      pfBase !== undefined && pfHasCarbs
        ? this.formPfPercent === "30"
          ? PROTEIN_FAT_CONFIG.ESCALATED_PERCENT
          : PROTEIN_FAT_CONFIG.STARTING_PERCENT
        : undefined;

    const entry: LogEntry = {
      id: this.editId || crypto.randomUUID(),
      timestamp,
      entryContext: contextInfo.context,
      loggedAt: new Date(),
      minutesLag: contextInfo.minutesLag,
      periodTag: this.formPeriod,
      bloodGlucose: this.formGL ? parseFloat(this.formGL) : undefined,
      carbs: this.formCP ? parseFloat(this.formCP) : undefined,
      qaBase: this.formQABase ? parseFloat(this.formQABase) : undefined,
      qaCorrection: this.formQACorr ? parseFloat(this.formQACorr) : undefined,
      pfBase,
      pfPercent,
      biBase: this.formBIBase ? parseFloat(this.formBIBase) : undefined,
      biCorrection: this.formBICorr ? parseFloat(this.formBICorr) : undefined,
      ketones: this.formKet ? parseFloat(this.formKet) : undefined,
      exercise: exerciseData,
      sickDay: sickDayData,
      notes: this.formNote || undefined,
      // Settings snapshot - capture ratios used at entry time
      cpRatioUsed,
      correctionFactorUsed,
      createdAt: this.editEntry?.createdAt || new Date(),
      updatedAt: new Date(),
    };

    try {
      // Use saveEntry from operations.ts to ensure sync metadata is set
      await saveEntry(entry);
      if (addAnother) {
        // Stay on Add so the user can batch-log more entries on the same
        // backdated day. Refresh the entries list so the just-saved entry
        // appears in "Recent Entries" and IOB recalculates.
        await this._loadEntries();
        this._resetFormForAnother();
        this.saving = false;
      } else {
        // Keep Today on the entry's date (overrides the default reset-to-today
        // on mount) so the user lands on the date they just logged for.
        selectedDateService.set(timestamp);
        selectedDateService.preserveOnNextMount();
        Router.go("/");
      }
    } catch (error) {
      console.error("Failed to save entry:", error);
      const message = error instanceof Error ? error.message : String(error);
      if (message.toLowerCase().includes("archived month")) {
        this.archiveError = message;
      }
      this.saving = false;
    }
  }

  /**
   * Clear entry-specific form fields while keeping the backdated date/time,
   * so the user can quickly log another event on the same past day.
   */
  private async _resetFormForAnother() {
    this.formGL = "";
    this.formCP = "";
    this.formQABase = "";
    this.formQACorr = "";
    this.formPfEnabled = false;
    this.formPfPercent = "20";
    this.formPfBase = "";
    this.suggestedPf = 0;
    this._pfNotesPrefillKey = "";
    this.formBIBase = "";
    this.formBICorr = "";
    this.formKet = "";
    this.formNote = "";
    this.selectedCategories = new Set();
    this.formExerciseDuration = "";
    this.archiveError = "";
    this.suggestedCorrection = 0;
    this.rawCorrection = 0;
    this.suggestedQABase = 0;
    this.calculatedCP = 0;
    this.correctionBlockedByIOB = false;
    this.periodManuallySelected = false;
    this._dateTimeManuallyChanged = true;

    this._refreshEntryContext();
    await this._calculateActiveIOB();
    this._calculateSuggestions();
    this._checkSickStatus();
    await this._updateUsedMarkers();
  }

  private async _deleteEntry() {
    if (!this.editId) return;

    // Prevent deleting locked CGM entries
    if (this.isLockedEntry) {
      console.warn("Cannot delete: this is a locked CGM entry");
      return;
    }

    this.archiveError = "";

    try {
      // Use deleteEntry from operations.ts to ensure sync metadata is set (soft delete)
      await deleteEntry(this.editId);
      Router.go("/");
    } catch (error) {
      console.error("Failed to delete entry:", error);
      const message = error instanceof Error ? error.message : String(error);
      if (message.toLowerCase().includes("archived month")) {
        this.archiveError = message;
        this.showDeleteConfirm = false;
      }
    }
  }

  private _confirmDelete() {
    this.showDeleteConfirm = true;
  }

  private _cancelDelete() {
    this.showDeleteConfirm = false;
  }

  private _getSickDayRecommendations() {
    if (!this.selectedCategories.has("sick")) return null;

    // Calculate hours since sick started (approximate)
    const sickEntry = this.entries.find((e) => e.sickDay?.status === "sick");
    const hoursSick = sickEntry
      ? (new Date().getTime() - getTimestampMs(sickEntry.timestamp)) /
        (1000 * 60 * 60)
      : 0;

    const bg = this.formGL ? parseFloat(this.formGL) : undefined;
    const ket = this.formKet ? parseFloat(this.formKet) : undefined;

    return getSickDayRecommendation(bg, ket, hoursSick);
  }

  private _getExerciseRecommendations() {
    if (!this.selectedCategories.has("exercise") || !this.formExerciseDuration)
      return null;

    const exercise: ExerciseData = {
      type: this.formExerciseType,
      intensity: this.formExerciseIntensity,
      duration: parseInt(this.formExerciseDuration),
    };

    return getExerciseRecommendation(exercise, "before", this.formPeriod);
  }

  private _getRecentExerciseRecommendations() {
    const recentExercise = getRecentExercise(this.entries);
    if (recentExercise.length === 0) return null;

    // Get the most recent exercise
    const latest = recentExercise[0];
    if (!latest.exercise) return null;

    return getExerciseRecommendation(latest.exercise, "after", this.formPeriod);
  }

  private _getGlClass(value?: number): string {
    if (!value) return "";
    if (value < 4) return "gl-hypo";
    if (value < 5) return "gl-low";
    if (value <= 7) return "gl-target";
    if (value <= 10) return "gl-elevated";
    return "gl-high";
  }

  private _getKetClass(value?: number): string {
    if (!value) return "";
    if (value < 0.6) return "ket-normal";
    if (value < 1.5) return "ket-elevated";
    return "ket-high";
  }

  private _getEntryCardClass(entry: LogEntry): string {
    const classes = ["entry-card"];
    if (entry.bloodGlucose && entry.bloodGlucose < 4) classes.push("has-hypo");
    else if (entry.ketones && entry.ketones >= 1.5)
      classes.push("has-high-ket");
    return classes.join(" ");
  }

  private _getPeriodDisplayLabel(period: PeriodTag): string {
    switch (period) {
      case "3AM":
        return "Overnight (3am)";
      case "B":
        return "Breakfast";
      case "L":
        return "Lunch";
      case "EM":
        return "Dinner";
      case "BT":
        return "Before Bed";
      case "Other":
        return "Other";
      case "after-B":
        return "After Breakfast";
      case "after-L":
        return "After Lunch";
      case "after-EM":
        return "After Dinner";
      default:
        return "Other";
    }
  }

  /**
   * Short label for period buttons.
   */
  private _getPeriodShortLabel(period: PeriodTag): string {
    switch (period) {
      case "3AM":
        return "3AM";
      case "B":
        return "BF";
      case "L":
        return "L";
      case "EM":
        return "D";
      case "BT":
        return "BT";
      default:
        return "Other";
    }
  }

  /**
   * Render a main period button (3AM, B, L, EM, BT).
   */
  private _renderPeriodButton(marker: DafneMarker) {
    const isActive = this.formPeriod === marker;
    const isUsed = this.usedMarkersForDay.has(marker) && !this.editId;

    return html`
      <wa-button
        size="small"
        variant=${isActive ? "brand" : "neutral"}
        ?disabled=${isUsed}
        @click=${() => !isUsed && this._onPeriodChange(marker)}
        title=${isUsed
          ? `${this._getPeriodDisplayLabel(marker)} already logged today`
          : this._getPeriodDisplayLabel(marker)}
      >
        ${this._getPeriodShortLabel(marker)}
      </wa-button>
    `;
  }

  /**
   * Render an "other" button between main periods.
   * These represent "after-X" or just "Other" entries.
   */
  private _renderOtherButton(_afterPeriod: string) {
    // Highlight all "other" buttons when formPeriod is any "other" type
    const isActive =
      this.formPeriod === "Other" || this.formPeriod.startsWith("after-");

    return html`
      <wa-button
        size="small"
        variant=${isActive ? "brand" : "neutral"}
        @click=${() => this._onPeriodChange("Other")}
        title="Other (between meals)"
      >
        ·
      </wa-button>
    `;
  }

  private _renderSickDayRecommendations() {
    if (this._isSuggestionReadOnlyContext()) return "";
    const rec = this._getSickDayRecommendations();
    if (!rec) return "";

    const nextCheckTime = getNextCheckTime(new Date(), rec.nextBgCheckMinutes);

    return html`
      <div class="recommendations sick">
        <div class="rec-header">🤒 <strong>Sick Day Guidance</strong></div>
        ${rec.warnings.map(
          (w) => html`
            <div class="rec-item warning">
              <span class="icon">⚠️</span>
              <span>${w}</span>
            </div>
          `,
        )}
        ${rec.actions.map(
          (a) => html`
            <div class="rec-item action">
              <span class="icon">→</span>
              <span>${a}</span>
            </div>
          `,
        )}
        ${rec.checkKetones
          ? html`
              <div class="rec-item action">
                <span class="icon">🧪</span>
                <span
                  ><strong>Check ketones</strong> - important when sick</span
                >
              </div>
            `
          : ""}
        <div class="next-check">
          ⏰ <strong>Next BG check:</strong> ${formatNextCheckTime(
            rec.nextBgCheckMinutes,
          )}
          (at ${format(nextCheckTime, "HH:mm")})
        </div>
        ${rec.correctionMultiplier > 1
          ? html`
              <div class="rec-item action" style="margin-top: 0.5rem;">
                <span class="icon">💉</span>
                <span
                  >Consider ${Math.round((rec.correctionMultiplier - 1) * 100)}%
                  extra on correction doses</span
                >
              </div>
            `
          : ""}
      </div>
    `;
  }

  private _renderExerciseRecommendations() {
    if (this._isSuggestionReadOnlyContext()) return "";
    const rec = this._getExerciseRecommendations();
    if (!rec) return "";

    return html`
      <div class="recommendations exercise">
        <div class="rec-header">
          🏃 <strong>Pre-Exercise Adjustment</strong>
        </div>
        <div class="rec-item action">
          <span class="icon">💉</span>
          <span
            >Reduce quick-acting insulin by
            <strong>${rec.qaReduction}%</strong></span
          >
        </div>
        <div class="rec-item action">
          <span class="icon">🍞</span>
          <span
            >OR add <strong>${rec.extraCarbs}g</strong> uncovered carbs</span
          >
        </div>
        <div class="rec-item" style="font-size: 0.75rem; color: #6c757d;">
          <span class="icon">ℹ️</span>
          <span>${rec.reason}</span>
        </div>
        ${rec.warnings.map(
          (w) => html`
            <div class="rec-item warning">
              <span class="icon">⚠️</span>
              <span>${w}</span>
            </div>
          `,
        )}
      </div>
    `;
  }

  private _renderRecentExerciseWarning() {
    if (this._isSuggestionReadOnlyContext()) return "";
    const rec = this._getRecentExerciseRecommendations();
    if (!rec) return "";

    const recentExercise = getRecentExercise(this.entries);
    if (recentExercise.length === 0) return "";

    const latest = recentExercise[0];
    const hoursAgo =
      Math.round(
        ((new Date().getTime() - getTimestampMs(latest.timestamp)) /
          (1000 * 60 * 60)) *
          10,
      ) / 10;

    return html`
      <div
        class="recommendations exercise"
        style="background: #e8f4f8; border-color: #17a2b8;"
      >
        <div class="rec-header">
          🏃 <strong>Recent Exercise Effect</strong>
          <span
            style="font-weight: normal; font-size: 0.75rem; color: #6c757d;"
          >
            (${hoursAgo}h ago)
          </span>
        </div>
        <div class="rec-item action">
          <span class="icon">💉</span>
          <span
            >Consider reducing correction by
            <strong>${rec.qaReduction}%</strong></span
          >
        </div>
        <div class="rec-item" style="font-size: 0.75rem; color: #6c757d;">
          <span class="icon">ℹ️</span>
          <span>${rec.reason}</span>
        </div>
        ${rec.warnings.map(
          (w) => html`
            <div class="rec-item warning">
              <span class="icon">⚠️</span>
              <span>${w}</span>
            </div>
          `,
        )}
      </div>
    `;
  }

  private _getKetoneAlerts() {
    const ket = this.formKet ? parseFloat(this.formKet) : undefined;
    if (!ket) return null;

    const bg = this.formGL ? parseFloat(this.formGL) : undefined;
    return getKetoneAlert(ket, bg);
  }

  private _getBgKetoneWarning() {
    const bg = this.formGL ? parseFloat(this.formGL) : undefined;
    if (!bg) return null;

    const ket = this.formKet ? parseFloat(this.formKet) : undefined;
    return getBgKetoneAlert(bg, ket);
  }

  private _renderKetoneAlerts() {
    if (this._isSuggestionReadOnlyContext()) return "";
    const alert = this._getKetoneAlerts();
    if (!alert || alert.level === "normal") return "";

    const cssClass =
      alert.level === "danger"
        ? "ketone-danger"
        : alert.level === "high"
          ? "ketone-high"
          : "ketone";

    const icon =
      alert.level === "danger" ? "🚨" : alert.level === "high" ? "⚠️" : "🧪";

    return html`
      <div class="recommendations ${cssClass}">
        <div class="rec-header">${icon} <strong>${alert.title}</strong></div>
        ${alert.warnings.map(
          (w) => html`
            <div class="rec-item warning">
              <span class="icon">⚠️</span>
              <span>${w}</span>
            </div>
          `,
        )}
        ${alert.actions.map(
          (a) => html`
            <div class="rec-item action">
              <span class="icon">→</span>
              <span>${a}</span>
            </div>
          `,
        )}
        ${alert.recheckMinutes > 0
          ? html`
              <div class="rec-item" style="margin-top: 0.5rem;">
                <span class="icon">⏰</span>
                <span
                  ><strong>Recheck ketones:</strong> ${formatNextCheckTime(
                    alert.recheckMinutes,
                  )}</span
                >
              </div>
            `
          : ""}
        ${alert.extraCorrectionPercent > 0
          ? html`
              <div class="rec-item action">
                <span class="icon">💉</span>
                <span
                  >Consider
                  <strong>${alert.extraCorrectionPercent}%</strong> extra on
                  correction dose</span
                >
              </div>
            `
          : ""}
      </div>
    `;
  }

  private _renderBgKetoneWarning() {
    if (this._isSuggestionReadOnlyContext()) return "";
    const warning = this._getBgKetoneWarning();
    if (!warning || !warning.shouldCheckKetones) return "";

    return html`
      <div class="recommendations bg-warning">
        <div class="rec-header">
          🧪 <strong>Ketone Check Recommended</strong>
        </div>
        <div class="rec-item action">
          <span class="icon">→</span>
          <span>${warning.reason}</span>
        </div>
      </div>
    `;
  }

  private _renderEntry(entry: LogEntry) {
    const qaTotal = getQATotal(entry);
    const biTotal = entry.biBase !== undefined ? entry.biBase : undefined;

    return html`
      <div class=${this._getEntryCardClass(entry)}>
        <div class="entry-header">
          <span class="entry-time">${format(entry.timestamp, "HH:mm")}</span>
          <span class="entry-period"
            >${this._getPeriodDisplayLabel(entry.periodTag)}</span
          >
        </div>
        <div class="entry-data">
          ${entry.bloodGlucose
            ? html`
                <div class="entry-field">
                  <span class="label">GL</span>
                  <span class="value ${this._getGlClass(entry.bloodGlucose)}"
                    >${entry.bloodGlucose.toFixed(1)}</span
                  >
                </div>
              `
            : ""}
          ${entry.carbs
            ? html`
                <div class="entry-field">
                  <span class="label">CP</span>
                  <span class="value">${entry.carbs}g</span>
                </div>
              `
            : ""}
          ${qaTotal !== undefined
            ? html`
                <div class="entry-field">
                  <span class="label">Quick</span>
                  <span class="value"
                    >${formatInsulinDose(
                      entry.qaBase,
                      entry.qaCorrection,
                    )}u</span
                  >
                </div>
              `
            : ""}
          ${biTotal !== undefined
            ? html`
                <div class="entry-field">
                  <span class="label">Long</span>
                  <span class="value">${entry.biBase}u</span>
                </div>
              `
            : ""}
          ${entry.ketones !== undefined
            ? html`
                <div class="entry-field">
                  <span class="label">Ket</span>
                  <span class="value ${this._getKetClass(entry.ketones)}"
                    >${entry.ketones.toFixed(1)}</span
                  >
                </div>
              `
            : ""}
        </div>
      </div>
    `;
  }

  /**
   * Render entries grouped by date with headers (Today, Yesterday, or date)
   */
  private _renderEntriesGroupedByDate() {
    const now = new Date();
    const today = startOfDay(now);
    const yesterday = startOfDay(
      new Date(today.getTime() - 24 * 60 * 60 * 1000),
    );

    const getTs = (t: Date | string | number | unknown): number => {
      if (t instanceof Date) return t.getTime();
      if (typeof t === "string") return new Date(t).getTime();
      if (typeof t === "number") return t;
      return new Date(t as string).getTime();
    };

    // Group entries by date
    const grouped = new Map<string, LogEntry[]>();
    for (const entry of this.entries) {
      const entryDate = startOfDay(new Date(getTs(entry.timestamp)));
      const key = entryDate.toISOString();
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(entry);
    }

    // Sort keys descending (most recent first)
    const sortedKeys = [...grouped.keys()].sort(
      (a, b) => new Date(b).getTime() - new Date(a).getTime(),
    );

    const getDateLabel = (dateKey: string): string => {
      const date = new Date(dateKey);
      if (date.getTime() === today.getTime()) return "Today";
      if (date.getTime() === yesterday.getTime()) return "Yesterday";
      return format(date, "EEEE, d MMM");
    };

    return html`
      ${sortedKeys.map((key) => {
        const entries = grouped.get(key)!;
        // Sort entries within day by time ascending
        entries.sort((a, b) => getTs(a.timestamp) - getTs(b.timestamp));
        return html`
          <div class="date-header">${getDateLabel(key)}</div>
          ${entries.map((e) => this._renderEntry(e))}
        `;
      })}
    `;
  }

  private _getGlStatus(): { class: string; label: string } | null {
    const gl = parseFloat(this.formGL);
    if (Number.isNaN(gl) || gl <= 0) return null;
    if (gl < 4) return { class: "hypo", label: "HYPO" };
    if (gl < 5) return { class: "low", label: "Low" };
    if (gl <= 7) return { class: "target", label: "Target" };
    if (gl <= 10) return { class: "elevated", label: "Elevated" };
    return { class: "high", label: "High" };
  }

  private _getQATotal(): number {
    const base = parseFloat(this.formQABase) || 0;
    const corr = parseFloat(this.formQACorr) || 0;
    return base + corr;
  }

  private _getBITotal(): number {
    const base = parseFloat(this.formBIBase) || 0;
    const corr = parseFloat(this.formBICorr) || 0;
    return base + corr;
  }

  /**
   * Check for dangerous QA insulin dose
   * Returns warning info if dose is dangerous, null otherwise
   */
  private _getQADoseWarning(): {
    level: "danger" | "warning";
    message: string;
    predictedGL?: number;
  } | null {
    const qaBase = parseFloat(this.formQABase) || 0;
    const qaCorr = parseFloat(this.formQACorr) || 0;
    const qaTotal = qaBase + qaCorr;
    if (qaTotal <= 0) return null;

    const gl = parseFloat(this.formGL);
    const carbsGrams = parseFloat(this.formCP) || 0;
    const carbsCP = Math.max(0, carbsGrams / 10);
    const correctionFactor = this.settings.correctionFactor || 3;
    const increment = this.settings.doseIncrement || 0.5;

    const formatUnits = (value: number) => {
      const rounded = Math.round(value * 10) / 10;
      return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
    };

    // WARNING: Unusually high QA dose (> 30 units is rare for most people)
    if (qaTotal > 30) {
      return {
        level: "warning",
        message: `Warning: ${qaTotal}u is an unusually high dose. Please verify this is correct.`,
      };
    }

    if (Number.isNaN(gl) || gl <= 0) return null;

    // Meal insulin paired with carbs is considered covered by carb rise.
    // Hypo-risk projection only includes correction/excess insulin effects.
    const ratioKey =
      PERIOD_TAG_INFO[this.formPeriod]?.ratioFrom || this.formPeriod;
    const expectedMealDose =
      carbsCP > 0
        ? calculateQADoseForCarbs(
            carbsCP,
            this.settings.cpRatios,
            ratioKey,
            increment,
          )
        : 0;

    const currentCorrectionUnits =
      carbsCP > 0 ? Math.max(0, qaCorr) : Math.max(0, qaTotal);
    const uncoveredMealUnits =
      carbsCP > 0 ? Math.max(0, qaBase - expectedMealDose) : 0;
    const correctionDrivenUnits =
      currentCorrectionUnits + this.activeCorrectionIOB + uncoveredMealUnits;

    if (correctionDrivenUnits <= 0) {
      return null;
    }

    const historicalCarbBuffer = this.activeCarbsOnBoardCP * 2.5;
    const predictedGL =
      gl + historicalCarbBuffer - correctionDrivenUnits * correctionFactor;

    const correctionIobNote =
      this.activeCorrectionIOB > 0
        ? ` (incl. ${formatUnits(this.activeCorrectionIOB)}u correction IOB)`
        : "";
    const carbsOnBoardNote =
      this.activeCarbsOnBoardCP > 0
        ? ` Recent carbs on board (+~${historicalCarbBuffer.toFixed(1)} mmol/L) are included.`
        : "";

    if (!Number.isNaN(predictedGL) && predictedGL < 2.5) {
      return {
        level: "danger",
        message: `DANGER: ${formatUnits(correctionDrivenUnits)}u correction/excess insulin${correctionIobNote} could drop GL to ~${predictedGL.toFixed(1)} mmol/L (severe hypo risk). Meal-matched carb insulin is excluded.${carbsOnBoardNote}`,
        predictedGL,
      };
    }

    if (!Number.isNaN(predictedGL) && predictedGL < 4) {
      return {
        level: "warning",
        message: `Warning: ${formatUnits(correctionDrivenUnits)}u correction/excess insulin${correctionIobNote} may drop GL to ~${predictedGL.toFixed(1)} mmol/L (hypo risk). Meal-matched carb insulin is excluded.${carbsOnBoardNote}`,
        predictedGL,
      };
    }

    return null;
  }

  /**
   * Warn when entered QA dose no longer aligns with current calculated guidance.
   * This catches stale manual doses after GL/carb/context changes.
   */
  private _getQAMismatchWarning(): {
    level: "warning";
    message: string;
  } | null {
    if (!this.selectedCategories.has("quick")) return null;
    if (this._isSuggestionReadOnlyContext()) return null;
    if (this.settingsMissing.length > 0) return null;

    const increment = this.settings.doseIncrement || 0.5;
    const base = parseFloat(this.formQABase) || 0;
    const corr = parseFloat(this.formQACorr) || 0;
    const hasEnteredDose = base !== 0 || corr !== 0;
    if (!hasEnteredDose) return null;

    const messages: string[] = [];
    const carbs = parseFloat(this.formCP);
    const gl = parseFloat(this.formGL);

    const formatUnits = (value: number) =>
      Number.isInteger(value) ? `${value}` : value.toFixed(1);

    // Base mismatch: compare entered meal dose against current carb-based suggestion.
    if (!Number.isNaN(carbs) && carbs > 0 && this.suggestedQABase > 0) {
      const baseDiff = Math.abs(base - this.suggestedQABase);
      if (baseDiff >= increment / 2) {
        messages.push(
          `Base QA entered (${formatUnits(base)}u) differs from suggested ${formatUnits(this.suggestedQABase)}u.`,
        );
      }
    }

    // Correction mismatch: compare entered correction against current GL/IOB suggestion.
    if (!Number.isNaN(gl) && gl > 0 && this.settings.correctionFactor) {
      if (this.correctionBlockedByIOB && corr > 0) {
        messages.push(
          `Correction QA entered (${formatUnits(corr)}u) while ${formatUnits(this.activeIOB)}u IOB is still active in no-stacking mode.`,
        );
      } else {
        const corrDiff = Math.abs(corr - this.suggestedCorrection);
        if (corrDiff >= increment / 2) {
          messages.push(
            `Correction QA entered (${formatUnits(corr)}u) differs from suggested ${formatUnits(this.suggestedCorrection)}u.`,
          );
        }
      }
    }

    if (messages.length === 0) return null;

    return {
      level: "warning",
      message: `${messages.join(" ")} Review before saving.`,
    };
  }

  private _renderQAMismatchWarning() {
    const warning = this._getQAMismatchWarning();
    if (!warning) return nothing;
    return html`
      <wa-callout variant=${warning.level} open style="margin-top: 0.5rem;">
        <wa-icon slot="icon" name="triangle-exclamation"></wa-icon>
        ${warning.message}
      </wa-callout>
    `;
  }

  private _renderQADoseWarning() {
    const warning = this._getQADoseWarning();
    if (!warning) return nothing;
    return html`
      <wa-callout variant=${warning.level} open style="margin-top: 0.5rem;">
        <wa-icon
          slot="icon"
          name=${warning.level === "danger"
            ? "skull-crossbones"
            : "triangle-exclamation"}
        ></wa-icon>
        ${warning.message}
      </wa-callout>
    `;
  }

  /**
   * Render the OZDAFNE Extra Protein/Fat (P/F) quick-acting insulin sub-form,
   * sitting under the Quick section. Source: OZDAFNE Protein and Fat Dosing
   * handout, Version 4, August 2025. P/F covers the delayed GL rise from high
   * protein/fat meals (verified at 3h and 5h post-meal).
   */
  private _renderProteinFatSection() {
    const grams = parseFloat(this.formCP);
    const hasCarbs = !Number.isNaN(grams) && grams > 0;
    const enabled = this.formPfEnabled;
    return html`
      <div class="pf-section" style="margin-top: 0.75rem;">
        <label
          style="display: flex; align-items: center; gap: 0.5rem; cursor: pointer;"
        >
          <wa-switch
            ?checked=${enabled}
            @change=${(e: Event) =>
              this._onPfToggleChange(
                (e.target as HTMLInputElement).checked,
              )}
          ></wa-switch>
          <span style="font-weight: 500;">High protein/fat meal</span>
        </label>
        <div class="field-help" style="margin-top: 0.25rem;">
          Adds Extra Protein/Fat quick-acting insulin (P/F) — based on OZDAFNE
          guidelines.
        </div>
        ${enabled
          ? html`
              <div
                style="margin-top: 0.5rem; padding: 0.75rem; background: #faf6ff; border-radius: 0.375rem; border: 1px solid #e9d8fd;"
              >
                ${hasCarbs
                  ? html`
                      <div
                        style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;"
                      >
                        <span style="font-weight: 500;">P/F dose:</span>
                        <wa-radio-group
                          value=${this.formPfPercent}
                          orientation="horizontal"
                          @change=${(e: Event) => {
                            const v = (e.target as HTMLInputElement).value as
                              | "20"
                              | "30";
                            this._onPfPercentChange(v);
                          }}
                        >
                          <wa-radio value="20">20%</wa-radio>
                          <wa-radio value="30">30%</wa-radio>
                        </wa-radio-group>
                      </div>
                      <div class="field-help" style="margin-top: 0.25rem;">
                        Start at 20% of your QA-for-CPs dose. If GL is still
                        above target at 3h and 5h on two occasions with the
                        same meal, escalate to 30% (OZDAFNE guidance).
                      </div>
                    `
                  : html`
                      <div class="field-help">
                        No-carb meal: P/F = your meal-time ratio × 1 CP. The CP
                        row will be recorded as <strong>0 + 1</strong>.
                      </div>
                    `}
                <div
                  class="insulin-row"
                  style="margin-top: 0.5rem; align-items: center;"
                >
                  <wa-input
                    type="number"
                    step=${this.settings.doseIncrement || 0.5}
                    min="0"
                    max="20"
                    inputmode="decimal"
                    placeholder="P/F units"
                    .value=${this.formPfBase}
                    @input=${(e: Event) =>
                      this._onPfBaseChange(
                        (e.target as HTMLInputElement).value,
                      )}
                  ></wa-input>
                  <span style="font-size: 0.85rem; color: #6c757d;">units</span>
                </div>
                ${this.suggestedPf > 0 &&
                !this._isSuggestionReadOnlyContext()
                  ? html`
                      <div class="field-help" style="margin-top: 0.5rem;">
                        Suggested P/F:
                        <strong>${this.suggestedPf}u</strong>
                        ${hasCarbs
                          ? html`(${this.formPfPercent}% of
                              ${this.suggestedQABase}u QA-for-CPs, rounded up)`
                          : html`(ratio × 1 CP)`}
                        <wa-button
                          size="small"
                          style="margin-left: 0.5rem;"
                          @click=${() => {
                            this.formPfBase = this.suggestedPf.toString();
                            this._maybePrefillPfNotes();
                            this.requestUpdate();
                          }}
                          >Apply</wa-button
                        >
                      </div>
                    `
                  : ""}
                ${parseFloat(this.formPfBase) > 0
                  ? html`
                      <div class="field-help" style="margin-top: 0.5rem;">
                        After this meal, check GL at
                        <strong
                          >${this._pfCheckLabel(
                            PROTEIN_FAT_CONFIG.PEAK_CHECK_HOURS[0],
                          )}</strong
                        >
                        and
                        <strong
                          >${this._pfCheckLabel(
                            PROTEIN_FAT_CONFIG.PEAK_CHECK_HOURS[1],
                          )}</strong
                        >. Avoid other food or quick-acting insulin in this
                        window so the result is interpretable.
                      </div>
                    `
                  : ""}
              </div>
            `
          : ""}
      </div>
    `;
  }

  /** Format an HH:MM label for the entry's planned timestamp + N hours. */
  private _pfCheckLabel(hoursAfter: number): string {
    const [h, m] = (this.formTime || "00:00").split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return `${hoursAfter}h`;
    const base = new Date(this.currentDate);
    base.setHours(h, m, 0, 0);
    const target = new Date(base.getTime() + hoursAfter * 60 * 60 * 1000);
    return `${hoursAfter}h (${format(target, "HH:mm")})`;
  }

  /**
   * Generate markdown representation of current form state for debugging
   */
  renderMarkdown(): string {
    const lines: string[] = [];
    const dateStr = format(this.currentDate, "EEEE, d MMMM yyyy");

    lines.push(`## ${this.editId ? "Edit Entry" : "Add Entry"}`);
    lines.push("");
    lines.push(`**Date:** ${dateStr}`);
    lines.push(
      `**Mode:** ${this.editId ? (this.isLockedEntry ? "View (Locked)" : "Edit") : "New Entry"}`,
    );
    lines.push(
      `**Context:** ${this.entryContext}${this.entryMinutesLag > 0 ? ` (${this.entryMinutesLag} min ago)` : ""}`,
    );
    lines.push("");

    lines.push("### Form State");
    lines.push("");
    lines.push(`- **Time:** ${this.formTime || "(not set)"}`);
    lines.push(
      `- **Period:** ${this.formPeriod} (${PERIOD_TAG_INFO[this.formPeriod].label})`,
    );
    lines.push(`- **GL:** ${this.formGL || "(empty)"}`);
    lines.push(`- **CP (Carbs):** ${this.formCP || "(empty)"}`);
    lines.push(`- **Quick Base:** ${this.formQABase || "(empty)"}`);
    lines.push(`- **Quick Correction:** ${this.formQACorr || "(empty)"}`);
    lines.push(`- **Long Base:** ${this.formBIBase || "(empty)"}`);
    lines.push(`- **Long Correction:** ${this.formBICorr || "(empty)"}`);
    lines.push(`- **Ketones:** ${this.formKet || "(empty)"}`);
    lines.push(`- **Note:** ${this.formNote || "(empty)"}`);
    lines.push("");

    lines.push("### Categories Selected");
    lines.push("");
    lines.push(
      `- Eating: ${this.selectedCategories.has("eating") ? "Yes" : "No"}`,
    );
    lines.push(
      `- Quick Insulin: ${this.selectedCategories.has("quick") ? "Yes" : "No"}`,
    );
    lines.push(
      `- Long Insulin: ${this.selectedCategories.has("long") ? "Yes" : "No"}`,
    );
    lines.push(
      `- Ketones: ${this.selectedCategories.has("ketone") ? "Yes" : "No"}`,
    );
    lines.push(
      `- Sick Day: ${this.selectedCategories.has("sick") ? "Yes" : "No"}`,
    );
    lines.push(
      `- Exercise: ${this.selectedCategories.has("exercise") ? "Yes" : "No"}`,
    );
    lines.push("");

    lines.push("### Calculations & Suggestions");
    lines.push("");
    lines.push(`- **IOB (Active):** ${this.activeIOB}u`);
    lines.push(`- **Correction IOB:** ${this.activeCorrectionIOB}u`);
    lines.push(`- **Carbs on Board (CP):** ${this.activeCarbsOnBoardCP}`);
    lines.push(`- **Suggested QA Base:** ${this.suggestedQABase}u`);
    lines.push(
      `- **Suggested Correction:** ${this.suggestedCorrection}u (raw: ${this.rawCorrection}u)`,
    );
    lines.push(
      `- **Correction Blocked by IOB:** ${this.correctionBlockedByIOB ? "Yes" : "No"}`,
    );
    lines.push(`- **Calculated CP:** ${this.calculatedCP}`);
    lines.push("");

    if (this.selectedCategories.has("exercise")) {
      lines.push("### Exercise");
      lines.push("");
      lines.push(`- **Type:** ${this.formExerciseType}`);
      lines.push(`- **Intensity:** ${this.formExerciseIntensity}`);
      lines.push(
        `- **Duration:** ${this.formExerciseDuration || "(not set)"} min`,
      );
      lines.push(`- **QA Reduction:** ${this.exerciseReduction}%`);
      lines.push("");
    }

    if (this.currentSickStatus) {
      lines.push("### Sick Day Status");
      lines.push("");
      lines.push(`- **Sick Day Multiplier:** ${this.sickDayMultiplier}`);
      lines.push("");
    }

    if (this.settingsMissing.length > 0) {
      lines.push("### Missing Settings");
      lines.push("");
      for (const setting of this.settingsMissing) {
        lines.push(`- ${setting}`);
      }
      lines.push("");
    }

    if (this.editEntry) {
      lines.push("### Original Entry (Edit Mode)");
      lines.push("");
      lines.push(`- **ID:** ${this.editEntry.id}`);
      lines.push(`- **Timestamp:** ${this.editEntry.timestamp}`);
      lines.push(`- **Period:** ${this.editEntry.periodTag}`);
      lines.push("");
    }

    return lines.join("\n");
  }

  render() {
    const hasGlucose = this._hasGlucose();
    const glStatus = this._getGlStatus();
    const qaTotal = this._getQATotal();
    const pfDose =
      this.formPfEnabled && parseFloat(this.formPfBase) > 0
        ? parseFloat(this.formPfBase)
        : 0;
    const qaGrandTotal = qaTotal + pfDose;
    const biTotal = this._getBITotal();
    const canSave =
      hasGlucose &&
      !this.isLockedEntry &&
      !this.isArchivedEntry &&
      this.entryContext !== "future";

    return html`
      <app-shell
        pageTitle=${this.editId ? "Edit Entry" : "Add Entry"}
        ?showSave=${hasGlucose}
        ?saveDisabled=${!canSave}
        ?saveLoading=${this.saving}
      >
        <div class="page-header">
          <h2>
            ${this.editId
              ? this.isLockedEntry
                ? "View Entry"
                : "Edit Entry"
              : "Add Entry"}
          </h2>
          <div style="display: flex; gap: 0.5rem;">
            ${this.editId && !this.isLockedEntry && !this.isArchivedEntry
              ? html`
                  <wa-button
                    variant="danger"
                    size="small"
                    @click=${this._confirmDelete}
                  >
                    Delete
                  </wa-button>
                `
              : ""}
            ${this.isLockedEntry
              ? html`<wa-button
                  variant="neutral"
                  size="small"
                  @click=${this._cancel}
                  >Close</wa-button
                >`
              : ""}
          </div>
        </div>

        ${this.isLockedEntry
          ? html`
              <wa-callout variant="warning" open style="margin: 1rem;">
                <wa-icon slot="icon" name="lock"></wa-icon>
                <strong>CGM-Imported Entry</strong>
                <div style="margin-top: 0.25rem;">
                  This entry was imported from your CGM device and cannot be
                  edited. You can view the data below.
                </div>
              </wa-callout>
            `
          : ""}
        ${this.isArchivedEntry
          ? html`
              <wa-callout variant="warning" open style="margin: 1rem;">
                <wa-icon slot="icon" name="box-archive"></wa-icon>
                <strong>Archived month — read only</strong>
                <div style="margin-top: 0.25rem;">
                  This entry falls within a month that has been archived to
                  long-term storage. Archived data cannot be edited or deleted.
                </div>
              </wa-callout>
            `
          : ""}
        ${this.archiveError
          ? html`
              <wa-callout variant="warning" open style="margin: 1rem;">
                <wa-icon slot="icon" name="box-archive"></wa-icon>
                <strong>Cannot save to archived month</strong>
                <div style="margin-top: 0.25rem;">${this.archiveError}</div>
              </wa-callout>
            `
          : ""}
        ${this.showDeleteConfirm
          ? html`
              <wa-callout variant="danger" open style="margin: 1rem;">
                <strong>Delete this entry?</strong>
                <div style="margin-top: 0.5rem;">
                  <wa-button size="small" @click=${this._cancelDelete}
                    >Cancel</wa-button
                  >
                  <wa-button
                    variant="danger"
                    size="small"
                    @click=${this._deleteEntry}
                    >Delete</wa-button
                  >
                </div>
              </wa-callout>
            `
          : ""}
        ${this.showStalePageDialog
          ? html`
              <wa-callout variant="warning" open style="margin: 1rem;">
                <wa-icon slot="icon" name="clock"></wa-icon>
                <strong>You've been away for a while</strong>
                <div style="margin-top: 0.25rem; font-size: 0.875rem;">
                  The date and time may be outdated. Would you like to reset to
                  now?
                </div>
                <div style="margin-top: 0.5rem;">
                  <wa-button size="small" @click=${this._handleStalePageKeep}
                    >Keep Current</wa-button
                  >
                  <wa-button
                    variant="brand"
                    size="small"
                    @click=${this._handleStalePageReset}
                    >Reset to Now</wa-button
                  >
                </div>
              </wa-callout>
            `
          : ""}

        <!-- Ratios Bar -->
        <div class="ratios-bar">
          <span class="ratios-label">My Ratios:</span>
          <span class="ratio-item">
            <span class="ratio-period">B:</span>
            <span
              class="ratio-value ${!this.settings.cpRatios?.B ? "not-set" : ""}"
            >
              ${formatRatioDisplay(this.settings.cpRatios?.B)}
            </span>
          </span>
          <span class="ratio-separator">|</span>
          <span class="ratio-item">
            <span class="ratio-period">L:</span>
            <span
              class="ratio-value ${!this.settings.cpRatios?.L ? "not-set" : ""}"
            >
              ${formatRatioDisplay(this.settings.cpRatios?.L)}
            </span>
          </span>
          <span class="ratio-separator">|</span>
          <span class="ratio-item">
            <span class="ratio-period">D:</span>
            <span
              class="ratio-value ${!this.settings.cpRatios?.EM
                ? "not-set"
                : ""}"
            >
              ${formatRatioDisplay(this.settings.cpRatios?.EM)}
            </span>
          </span>
          <span class="ratio-separator">|</span>
          <span class="ratio-item">
            <span class="ratio-period">CF:</span>
            <span
              class="ratio-value ${!this.settings.correctionFactor
                ? "not-set"
                : ""}"
            >
              ${this.settings.correctionFactor
                ? `${this.settings.correctionFactor}mmol/u`
                : "—"}
            </span>
          </span>
        </div>

        <div class="page-content">
          <!-- Timing Section (always visible) -->
          <div class="form-section">
            <div class="entry-context-row">
              <span class="entry-context-badge ${this.entryContext}">
                ${this._getEntryContextLabel()}
              </span>
              <span class="entry-context-meta">
                ${this._getEntryContextMeta()}
              </span>
            </div>

            <div class="timing-row">
              <label>Time</label>
              <wa-input
                type="date"
                size="small"
                .value=${format(this.currentDate, "yyyy-MM-dd")}
                max=${format(new Date(), "yyyy-MM-dd")}
                @input=${(e: Event) =>
                  this._onDateChange((e.target as HTMLInputElement).value)}
              ></wa-input>
              <wa-input
                type="time"
                size="small"
                .value=${this.formTime}
                @input=${(e: Event) =>
                  this._onTimeChange((e.target as HTMLInputElement).value)}
              ></wa-input>
            </div>

            ${this._isSuggestionReadOnlyContext()
              ? html`
                  <wa-callout
                    variant="neutral"
                    open
                    style="margin-top: 0.5rem;"
                  >
                    <wa-icon slot="icon" name="clock-rotate-left"></wa-icon>
                    <strong>Backdating</strong> — predictions and
                    recommendations disabled
                  </wa-callout>
                `
              : ""}

            <label>Period</label>
            <div class="period-selector">
              <wa-button-group>
                ${this._renderPeriodButton("3AM")}
                ${this._renderOtherButton("after-3AM")}
                ${this._renderPeriodButton("B")}
                ${this._renderOtherButton("after-B")}
                ${this._renderPeriodButton("L")}
                ${this._renderOtherButton("after-L")}
                ${this._renderPeriodButton("EM")}
                ${this._renderOtherButton("after-EM")}
                ${this._renderPeriodButton("BT")}
              </wa-button-group>
            </div>
          </div>

          <!-- GL Input Section -->
          <div class="gl-input-section">
            <div class="gl-input-row">
              <wa-input
                class="primary-gl-input"
                type="number"
                step="0.1"
                min="1"
                max="33"
                inputmode="decimal"
                placeholder="GL"
                .value=${this.formGL}
                @input=${(e: Event) =>
                  this._onGLChange((e.target as HTMLInputElement).value)}
              ></wa-input>
              <span class="gl-unit">mmol/L</span>
              ${glStatus
                ? html`<span class="gl-status ${glStatus.class}"
                    >${glStatus.label}</span
                  >`
                : ""}
            </div>
          </div>

          <!-- Category Selector -->
          <div class="category-selector">
            <div class="category-label">What are you recording?</div>
            <div class="category-buttons">
              <wa-button
                size="small"
                variant=${this.selectedCategories.has("eating")
                  ? "brand"
                  : "neutral"}
                ?disabled=${!hasGlucose}
                @click=${() => this._toggleCategory("eating")}
              >
                🍽️ Eating
              </wa-button>
              <wa-button
                size="small"
                variant=${this.selectedCategories.has("quick")
                  ? "brand"
                  : "neutral"}
                ?disabled=${!hasGlucose}
                @click=${() => this._toggleCategory("quick")}
              >
                💉 Quick
              </wa-button>
              <wa-button
                size="small"
                variant=${this.selectedCategories.has("long")
                  ? "brand"
                  : "neutral"}
                ?disabled=${!hasGlucose}
                @click=${() => this._toggleCategory("long")}
              >
                💊 Long
              </wa-button>
              <wa-button
                size="small"
                variant=${this.selectedCategories.has("ketone")
                  ? "brand"
                  : "neutral"}
                ?disabled=${!hasGlucose}
                @click=${() => this._toggleCategory("ketone")}
              >
                🧪 Ketone
              </wa-button>
              <wa-button
                size="small"
                variant=${this.selectedCategories.has("sick")
                  ? "brand"
                  : "neutral"}
                ?disabled=${!hasGlucose}
                @click=${() => this._toggleCategory("sick")}
              >
                🤒 Sick
              </wa-button>
              <wa-button
                size="small"
                variant=${this.selectedCategories.has("exercise")
                  ? "brand"
                  : "neutral"}
                ?disabled=${!hasGlucose}
                @click=${() => this._toggleCategory("exercise")}
              >
                🏃 Exercise
              </wa-button>
            </div>
            ${!hasGlucose
              ? html`<div class="field-help" style="margin-top: 0.5rem;">
                  Enter glucose reading first to unlock options.
                </div>`
              : ""}
          </div>

          <!-- Eating Section -->
          ${this.selectedCategories.has("eating")
            ? html`
                <div class="dynamic-section">
                  <div class="section-header">🍽️ Eating</div>
                  <div class="form-row">
                    <label
                      >Carbs <small>(g)</small>${this.calculatedCP > 0
                        ? html` = <strong>${this.calculatedCP} CP</strong>`
                        : ""}</label
                    >
                    <wa-input
                      type="number"
                      step="1"
                      min="0"
                      max="500"
                      inputmode="numeric"
                      placeholder="e.g. 45"
                      .value=${this.formCP}
                      @input=${(e: Event) =>
                        this._onCPChange((e.target as HTMLInputElement).value)}
                    ></wa-input>
                  </div>
                  ${this.suggestedQABase > 0
                    ? html`
                        <div class="field-help">
                          Suggested: ${this.calculatedCP} CP ×
                          ${this._getUnitsPerCP(
                            PERIOD_TAG_INFO[this.formPeriod]?.ratioFrom ||
                              this.formPeriod,
                          )}u/CP
                          =
                          <strong>${this.suggestedQABase}u</strong>
                          ${!this._isSuggestionReadOnlyContext()
                            ? html`<wa-button
                                size="small"
                                style="margin-left: 0.5rem;"
                                @click=${() => {
                                  this.formQABase =
                                    this.suggestedQABase.toString();
                                  if (!this.selectedCategories.has("quick")) {
                                    this._toggleCategory("quick");
                                  }
                                }}
                                >Apply</wa-button
                              >`
                            : ""}
                        </div>
                      `
                    : ""}
                  ${this._renderProteinFatSection()}
                </div>
              `
            : ""}

          <!-- Correction suggestion prompt when Quick not selected -->
          ${!this.selectedCategories.has("quick") &&
          this.rawCorrection > 0 &&
          this.formGL
            ? html`
                <wa-callout
                  variant=${this.correctionBlockedByIOB ? "warning" : "brand"}
                  open
                  style="margin-bottom: 1rem;"
                >
                  <wa-icon
                    slot="icon"
                    name=${this.correctionBlockedByIOB
                      ? "layer-group"
                      : "syringe"}
                  ></wa-icon>
                  ${this.correctionBlockedByIOB
                    ? html`
                        <strong>Correction May Be Needed</strong>
                        <div style="margin-top: 0.25rem; font-size: 0.85rem;">
                          GL ${this.formGL} suggests a
                          <strong>${this.rawCorrection}u</strong> correction,
                          but ${this.activeIOB}u IOB is still active.
                        </div>
                        <div style="margin-top: 0.25rem; font-size: 0.85rem;">
                          No-stacking mode is on: dose for carbs as usual, but
                          do not add correction insulin until IOB has mostly
                          cleared.
                        </div>
                        <div style="margin-top: 0.5rem;">
                          <wa-button
                            size="small"
                            variant="brand"
                            @click=${() => this._toggleCategory("quick")}
                            >Add Quick Insulin</wa-button
                          >
                        </div>
                      `
                    : html`
                        <strong>Correction Suggested</strong>
                        <div style="margin-top: 0.25rem;">
                          GL ${this.formGL} suggests a
                          <strong>${this.rawCorrection}u</strong> correction
                          dose.
                        </div>
                        <div style="margin-top: 0.5rem;">
                          <wa-button
                            size="small"
                            variant="brand"
                            @click=${() => {
                              if (!this.selectedCategories.has("quick")) {
                                this._toggleCategory("quick");
                              }
                              this.formQACorr = this.rawCorrection.toString();
                            }}
                            >Apply ${this.rawCorrection}u Correction</wa-button
                          >
                        </div>
                      `}
                </wa-callout>
              `
            : ""}

          <!-- Quick Insulin Section -->
          ${this.selectedCategories.has("quick")
            ? html`
                <div class="dynamic-section">
                  <div class="section-header">💉 Quick-Acting Insulin</div>
                  <div class="insulin-row">
                    <wa-input
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      inputmode="decimal"
                      placeholder="Base"
                      .value=${this.formQABase}
                      @input=${(e: Event) =>
                        this._onQABaseChange(
                          (e.target as HTMLInputElement).value,
                        )}
                    ></wa-input>
                    <span class="insulin-operator">+</span>
                    <wa-input
                      type="number"
                      step="0.5"
                      min="-20"
                      max="20"
                      inputmode="decimal"
                      placeholder="Corr"
                      .value=${this.formQACorr}
                      @input=${(e: Event) =>
                        this._onQACorrChange(
                          (e.target as HTMLInputElement).value,
                        )}
                    ></wa-input>
                    ${pfDose > 0
                      ? html`<span
                          class="insulin-operator"
                          title="Extra Protein/Fat dose set in the Eating section"
                          >+ ${pfDose} P/F</span
                        >`
                      : ""}
                    ${qaGrandTotal > 0
                      ? html`<span class="insulin-total"
                          >= ${qaGrandTotal}u</span
                        >`
                      : ""}
                  </div>
                  ${pfDose > 0
                    ? html`
                        <div class="field-help" style="margin-top: 0.25rem;">
                          Includes
                          <strong>${pfDose}u</strong>
                          Extra Protein/Fat (set under Eating).
                        </div>
                      `
                    : ""}
                  ${this.suggestedCorrection !== 0
                    ? html`
                        <div class="field-help">
                          Correction suggestion:
                          ${this.suggestedCorrection > 0 ? "+" : ""}${this
                            .suggestedCorrection}u
                          (GL ${this.formGL} → target
                          ${(
                            (this.settings.targetBgLow +
                              this.settings.targetBgHigh) /
                            2
                          ).toFixed(1)})
                          ${!this._isSuggestionReadOnlyContext()
                            ? html`<wa-button
                                size="small"
                                style="margin-left: 0.5rem;"
                                @click=${() => {
                                  this.formQACorr =
                                    this.suggestedCorrection.toString();
                                }}
                                >Apply</wa-button
                              >`
                            : ""}
                        </div>
                      `
                    : ""}
                  ${this.correctionBlockedByIOB
                    ? html`
                        <wa-callout
                          variant="warning"
                          open
                          style="margin-top: 0.5rem;"
                        >
                          <wa-icon slot="icon" name="layer-group"></wa-icon>
                          <strong>Stacking Caution</strong>
                          <div style="margin-top: 0.25rem; font-size: 0.85rem;">
                            DAFNE no-stacking mode is active. Keep meal/carbs
                            dosing as planned, but do not add correction insulin
                            while IOB is still active.
                          </div>
                          <div style="margin-top: 0.5rem;">
                            Raw correction:
                            <strong>${this.rawCorrection}u</strong>
                            (GL ${this.formGL} → target
                            ${(
                              (this.settings.targetBgLow +
                                this.settings.targetBgHigh) /
                              2
                            ).toFixed(1)})
                          </div>
                          <div style="margin-top: 0.25rem;">
                            Active IOB: <strong>${this.activeIOB}u</strong>
                          </div>
                          <div style="margin-top: 0.25rem; font-size: 0.85rem;">
                            Correction IOB component:
                            <strong>${this.activeCorrectionIOB}u</strong>
                          </div>
                        </wa-callout>
                      `
                    : this.activeIOB > 0
                      ? html`
                          <div class="field-help" style="margin-top: 0.5rem;">
                            <wa-icon
                              name="circle-info"
                              style="margin-right: 0.25rem;"
                            ></wa-icon>
                            Active IOB: ${this.activeIOB}u still working from
                            previous doses
                          </div>
                        `
                      : ""}
                  ${this._renderQAMismatchWarning()}
                  ${this._renderQADoseWarning()}
                </div>
              `
            : ""}

          <!-- Long Insulin Section -->
          ${this.selectedCategories.has("long")
            ? html`
                <div class="dynamic-section">
                  <div class="section-header">💊 Long-Acting Insulin</div>
                  ${this.biDuplicateWarning
                    ? html`
                        <wa-callout variant="warning" open>
                          <wa-icon
                            slot="icon"
                            name="triangle-exclamation"
                          ></wa-icon>
                          You've already logged ${this.existingBIDoseCount} BI
                          dose${this.existingBIDoseCount > 1 ? "s" : ""} today.
                          Your setting is
                          ${this.settings.biFrequency === "twice"
                            ? "twice"
                            : "once"}
                          daily.
                        </wa-callout>
                      `
                    : ""}
                  <div class="insulin-row">
                    <wa-input
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      inputmode="decimal"
                      placeholder="Base"
                      .value=${this.formBIBase}
                      @input=${(e: Event) =>
                        this._onBIBaseChange(
                          (e.target as HTMLInputElement).value,
                        )}
                    ></wa-input>
                    <span class="insulin-operator">+</span>
                    <wa-input
                      type="number"
                      step="0.5"
                      min="-20"
                      max="20"
                      inputmode="decimal"
                      placeholder="Adj"
                      .value=${this.formBICorr}
                      @input=${(e: Event) =>
                        this._onBICorrChange(
                          (e.target as HTMLInputElement).value,
                        )}
                    ></wa-input>
                    ${biTotal > 0
                      ? html`<span class="insulin-total">= ${biTotal}u</span>`
                      : ""}
                  </div>
                </div>
              `
            : ""}

          <!-- Ketone Section -->
          ${this.selectedCategories.has("ketone")
            ? html`
                <div class="dynamic-section">
                  <div class="section-header">🧪 Ketones</div>
                  <div class="form-row">
                    <wa-input
                      type="number"
                      step="0.1"
                      min="0"
                      max="10"
                      inputmode="decimal"
                      placeholder="e.g. 0.3"
                      .value=${this.formKet}
                      @input=${(e: Event) =>
                        this._onKetoneChange(
                          (e.target as HTMLInputElement).value,
                        )}
                    ></wa-input>
                    <span class="gl-unit">mmol/L</span>
                  </div>
                </div>
              `
            : ""}

          <!-- Sick Section -->
          ${this.selectedCategories.has("sick")
            ? html`
                <div class="dynamic-section">
                  <div class="section-header">🤒 Sick Day</div>
                  <div class="sick-status-text">
                    This entry is marked as <strong>sick</strong>. Untick sick
                    above when you're feeling better.
                  </div>
                  ${this._renderSickDayRecommendations()}
                </div>
              `
            : ""}

          <!-- Exercise Section -->
          ${this.selectedCategories.has("exercise")
            ? html`
                <div class="dynamic-section">
                  <div class="section-header">🏃 Exercise</div>

                  <div class="exercise-group">
                    <div class="exercise-group-label">Type</div>
                    <wa-button-group>
                      ${(Object.keys(EXERCISE_TYPE_INFO) as ExerciseType[]).map(
                        (type) => html`
                          <wa-button
                            size="small"
                            variant=${this.formExerciseType === type
                              ? "brand"
                              : "neutral"}
                            @click=${() => this._onExerciseTypeChange(type)}
                          >
                            ${EXERCISE_TYPE_INFO[type].label}
                          </wa-button>
                        `,
                      )}
                    </wa-button-group>
                  </div>

                  <div class="exercise-group">
                    <div class="exercise-group-label">Intensity</div>
                    <wa-button-group>
                      ${(
                        Object.keys(
                          EXERCISE_INTENSITY_INFO,
                        ) as ExerciseIntensity[]
                      ).map(
                        (intensity) => html`
                          <wa-button
                            size="small"
                            variant=${this.formExerciseIntensity === intensity
                              ? "brand"
                              : "neutral"}
                            @click=${() =>
                              this._onExerciseIntensityChange(intensity)}
                          >
                            ${EXERCISE_INTENSITY_INFO[intensity].label}
                          </wa-button>
                        `,
                      )}
                    </wa-button-group>
                  </div>

                  <div class="form-row">
                    <label>Duration (minutes)</label>
                    <wa-input
                      type="number"
                      min="5"
                      max="300"
                      step="5"
                      inputmode="numeric"
                      placeholder="e.g. 30"
                      .value=${this.formExerciseDuration}
                      @input=${(e: Event) =>
                        this._onExerciseDurationChange(
                          (e.target as HTMLInputElement).value,
                        )}
                    ></wa-input>
                  </div>
                </div>
              `
            : ""}

          <!-- Notes Section (always show when glucose entered) -->
          ${hasGlucose
            ? html`
                <div class="dynamic-section">
                  <div class="form-row">
                    <label>Note</label>
                    <wa-textarea
                      placeholder="Optional note..."
                      rows="2"
                      .value=${this.formNote}
                      @input=${(e: Event) =>
                        (this.formNote = (
                          e.target as HTMLTextAreaElement
                        ).value)}
                    ></wa-textarea>
                  </div>
                </div>
              `
            : ""}

          <!-- Guidance & Recommendations (before save) -->
          ${this.entryContext === "future"
            ? html`
                <wa-callout variant="danger" open>
                  <wa-icon slot="icon" name="clock-rotate-left"></wa-icon>
                  Entry time is in the future. Check date/time before saving.
                </wa-callout>
              `
            : ""}
          ${this.entryContext === "backdated"
            ? html`
                <wa-callout variant="warning" open>
                  <wa-icon slot="icon" name="book"></wa-icon>
                  Historical entry. Use for record completion and trend review.
                </wa-callout>
              `
            : ""}
          ${this.settingsMissing.length > 0
            ? html`
                <wa-callout variant="warning" open>
                  <wa-icon slot="icon" name="gear"></wa-icon>
                  <strong>Setup Required</strong><br />
                  Configure DAFNE settings for insulin suggestions.
                  <wa-button
                    size="small"
                    variant="brand"
                    style="margin-top: 0.5rem"
                    @click=${() => Router.go("/settings")}
                    >Go to Settings</wa-button
                  >
                </wa-callout>
              `
            : ""}

          <!-- Ketone alerts -->
          ${this._renderKetoneAlerts()}

          <!-- BG-based ketone check recommendation -->
          ${this._renderBgKetoneWarning()}

          <!-- Exercise recommendations -->
          ${this._renderExerciseRecommendations()}

          <!-- Recent exercise effect warning -->
          ${this._renderRecentExerciseWarning()}

          <!-- Save Section -->
          ${hasGlucose
            ? html`
                <div class="form-section">
                  <div class="form-actions">
                    <wa-button
                      variant="brand"
                      ?disabled=${this.isLockedEntry ||
                      this.entryContext === "future"}
                      @click=${() => this._saveEntry()}
                      >${this._isContextOnlyEntry()
                        ? "Save Marker"
                        : "Save Entry"}</wa-button
                    >
                    ${this.entryContext === "backdated" && !this.editId
                      ? html`
                          <wa-button
                            ?disabled=${this.isLockedEntry}
                            @click=${() => this._saveEntry(true)}
                            >Save &amp; Add Another</wa-button
                          >
                        `
                      : ""}
                  </div>
                </div>
              `
            : ""}

          <!-- Recent entries (past 24 hours) -->
          <div class="form-section">
            <div class="section-title">
              <wa-icon name="clock-rotate-left"></wa-icon>
              Recent Entries
            </div>
            ${this.entries.length === 0
              ? html`<p class="empty-entries">No recent entries</p>`
              : this._renderEntriesGroupedByDate()}
          </div>
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "add-entry-page": AddEntryPage;
  }
}
